'use client'

import PaymentOptions from '@/components/billing/payment-options'
import { PartnerSelector } from '@/components/partner/PartnerSelector'
import { HealthPlanSelector } from '@/components/health-plan/HealthPlanSelector'
import { Client } from '@/lib/api/clients/client-api'
import { ClientSelector } from '@/components/client/ClientSelector'
import {
    BillingPaymentMethod,
    BillingType,
    BillingPayerType,
} from '@/lib/api/common/interfaces/billing.interface'
import {
    createBilling,
    createInstallmentPlan,
    type CreateInstallmentPlanData,
    getBilling,
    removeBillingAttachment,
    updateBilling,
    uploadBillingAttachment,
    updateBillingPaymentSplits,
} from '@/lib/api/financial/financial-billing-api'
import { getCheckoutPaymentPolicy } from '@/lib/api/financial/financial-settings-api'
import {
    BillingOrigin,
    billingOriginPath,
    isAppointmentOrigin,
    getOriginArticles,
    getOriginLabels,
} from '@/lib/billing/billing-origin'
import { useServiceLabel } from '@/lib/hooks/use-service-label'
import { useEntityAccess } from '@/lib/hooks/use-entity-access'
import { useOfflineSettlementPolicy } from '@/lib/hooks/use-offline-settlement-policy'
import { localTodayInput } from '@/lib/utils/date-input'
import { isClosedBillingStatus } from '@/lib/billing/appointment-billing-state'
import {
    isEditableBillingStatus,
    uneditableBillingReason,
} from '@/lib/billing/billing-editable-status'
// Lista padrao de metodos de pagamento ate a fase BaaS introduzir resolucao dinamica
// por provider/subaccount ativo na organizacao.
const DEFAULT_PAYMENT_METHODS = [
    'PIX',
    'BOLETO',
    'CREDIT_CARD',
    'DEBIT_CARD',
    'CASH',
    'BANK_TRANSFER',
    'CHECK',
    'TERMINAL',
    'OTHER',
]
// Meios online cuja oferta depende da política de checkout (gateway). Sem
// conseguir ler a política (ex.: usuário sem acesso às Configurações
// Financeiras), eles NÃO são ofertados — fail-closed, nunca fail-open.
const POLICY_GATED_METHODS = ['PIX', 'CREDIT_CARD', 'DEBIT_CARD']
// Recebidos por fora do sistema: são os que a baixa automática quita ao salvar. Mesma
// lista de `OFFLINE_PAYMENT_METHODS` no backend.
const OFFLINE_SETTLEMENT_METHODS = ['CASH', 'TERMINAL', 'OTHER']
const NON_GATED_METHODS = DEFAULT_PAYMENT_METHODS.filter(
    m => !POLICY_GATED_METHODS.includes(m)
)
import {
    getTerminals,
    PaymentTerminal,
} from '@/lib/api/financial/financial-terminal-api'
import { ProfessionalSearchSelector } from '@/components/ProfessionalSearchSelector'
import { HospitalSearchSelector } from '@/components/HospitalSearchSelector'
import BillingComposition from '@/components/billing/billing-composition'
import {
    PayableRecipientPicker,
    TYPE_LABELS,
} from '@/components/payable-recipient-picker'
import { getSuppliers, Supplier } from '@/lib/api/suppliers/supplier-api'
import {
    FinancialCategoryItem,
    getFinancialCategories,
} from '@/lib/api/financial/financial-category-api'
import {
    CostCenterItem,
    getCostCenters,
} from '@/lib/api/financial/cost-center-api'
import {
    BankAccountItem,
    getBankAccounts,
} from '@/lib/api/financial/bank-account-api'
import { useAuth } from '@/store/hooks'
import { useResourcePermissions } from '@/lib/hooks/use-resource-permissions'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useBillingAiBridge } from './billing-ai-bridge'
import {
    MdArrowBack,
    MdAttachMoney,
    MdBlock,
    MdClose,
    MdDelete,
    MdLink,
    MdNotes,
    MdPayment,
    MdPerson,
    MdQrCode,
    MdReceipt,
    MdRepeat,
    MdSearch,
    MdStore,
    MdCreditCard,
    MdTrendingDown,
    MdTrendingUp,
    MdUploadFile,
} from 'react-icons/md'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { FormSection } from '@/components/form-section'
import {
    removePayoutBatchItem,
    submitPayoutBatch,
} from '@/lib/api/financial/payout-batch-api'
import PayoutBatchItemsList from '@/components/billing/payout-batch-items-list'
import {
    billingCategoryLabel,
    CUSTOM_CATEGORY_OPTION,
    isPayoutCategory,
    MANUAL_PAYABLE_CATEGORY_OPTIONS,
    manualPayableCategoryOptions,
} from '@/lib/billing/billing-categories'
import ConfirmModal from '@/components/confirm-modal'
import { toast } from 'react-toastify'

/** Anexo já persistido no billing (veio do backend, tem URL). */
interface AttachmentItem {
    url: string
    name: string
    description: string
}

/** Arquivo escolhido no formulário que ainda não foi enviado ao backend. */
interface PendingAttachment {
    file: File
    description: string
}

const PAYMENT_METHODS: { value: BillingPaymentMethod; label: string }[] = [
    { value: 'PIX', label: 'PIX' },
    { value: 'CREDIT_CARD', label: 'Cartão de Crédito' },
    { value: 'DEBIT_CARD', label: 'Cartão de Débito' },
    { value: 'CASH', label: 'Dinheiro' },
    /*{
        value: 'BANK_TRANSFER',
        label: 'Transferência Bancária (auto via conta digital)',
    },*/
    //{ value: 'BOLETO', label: 'Boleto' },
    //{ value: 'CHECK', label: 'Cheque' },
    { value: 'TERMINAL', label: 'Maquininha (terminal)' },
    { value: 'OTHER', label: 'Outro' },
]

interface FormData {
    type: BillingType | null
    clientId: number | null
    payerType: BillingPayerType
    healthPlanId: number | null
    partnerId: number | null
    professionalId: number | null
    supplierId: number | null
    supplierName: string
    hospitalId: number | null
    userId: number | null
    employeeId: number | null
    recipientType:
        | 'SUPPLIER'
        | 'CLIENT'
        | 'PARTNER'
        | 'PROFESSIONAL'
        | 'HOSPITAL'
        | 'USER'
        | 'EMPLOYEE'
    category: string
    financialCategoryId: number | null
    costCenterId: number | null
    bankAccountId: number | null
    description: string
    referenceCode: string
    amount: string
    discount: string
    interest: string
    dueDate: string
    /**
     * Pago em: o dia que a baixa automática do recebido por fora registra. Vazio = hoje.
     * Só vai ao servidor quando o campo aparece (ver `showPaidAt`).
     */
    paidAt: string
    paymentMethod: string
    /**
     * Parcelas no cartão combinadas com o pagador — NÃO é o parcelamento próprio
     * (`installmentTotal`, uma cobrança por parcela). É o número que trava o checkout.
     */
    installmentCount: number | null
    installmentNumber: string
    installmentTotal: string
    terminalInstallments: string
    recurringInterval: string
    recurringMaxOccurrences: string
    pixKey: string
    pixQrCode: string
    pixCopyPaste: string
    pixExpirationDate: string
    bankSlipUrl: string
    nossoNumero: string
    barcode: string
    digitableLine: string
    notes: string
    attachments: AttachmentItem[]
    enableRecurring: boolean
    recurringDueDay: number
}

const initialFormData: FormData = {
    type: null,
    clientId: null,
    payerType: 'CLIENT',
    healthPlanId: null,
    partnerId: null,
    professionalId: null,
    supplierId: null,
    supplierName: '',
    hospitalId: null,
    userId: null,
    employeeId: null,
    recipientType: 'SUPPLIER',
    category: '',
    financialCategoryId: null,
    costCenterId: null,
    bankAccountId: null,
    description: '',
    referenceCode: '',
    amount: '',
    discount: '',
    interest: '',
    dueDate: new Date().toISOString().split('T')[0],
    paidAt: '',
    paymentMethod: '',
    installmentCount: null,
    installmentNumber: '',
    installmentTotal: '',
    terminalInstallments: '1',
    recurringInterval: 'MONTHLY',
    recurringMaxOccurrences: '',
    pixKey: '',
    pixQrCode: '',
    pixCopyPaste: '',
    pixExpirationDate: '',
    bankSlipUrl: '',
    nossoNumero: '',
    barcode: '',
    digitableLine: '',
    notes: '',
    attachments: [],
    enableRecurring: false,
    recurringDueDay: 5,
}

/** Centavos -> "R$ 1.234,56". Usado no aviso de quanto falta distribuir. */
function formatCentsBrl(cents: number): string {
    return (cents / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    })
}

function parseCurrencyToInt(value: string): number {
    const cleaned = value.replace(/[^\d,]/g, '').replace(',', '.')
    const num = parseFloat(cleaned)
    return isNaN(num) ? 0 : Math.round(num * 100)
}

function formatCurrencyInput(value: string): string {
    const numbers = value.replace(/\D/g, '')
    if (!numbers) return ''
    const cents = parseInt(numbers, 10)
    return (cents / 100).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })
}

function intToCurrencyDisplay(cents: number): string {
    return (cents / 100).toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })
}

interface BillingsFormPageProps {
    defaultType?: 'RECEIVABLE' | 'PAYABLE'
    onBack?: () => void
    /**
     * Chamado após criar/editar. Recebe o id do billing para o caller decidir o
     * destino — o modal (PDV rápido) usa isso para abrir os detalhes, coisa que
     * não conseguia fazer quando o callback não recebia id nenhum.
     */
    onSuccess?: (billingId?: number) => void
    /**
     * Renderizado dentro de um modal (PDV rápido), fora da própria rota. Nesse
     * modo os parâmetros da URL NÃO são do billing: em `/person/clients/29502` o
     * `:id` é do cliente, e sem isto o form o interpretava como id de cobrança e
     * abria em modo edição carregando uma cobrança alheia.
     */
    isModal?: boolean
}

export default function BillingsFormPage({
    defaultType,
    onBack,
    onSuccess,
    isModal,
}: BillingsFormPageProps = {}) {
    const { user } = useAuth()
    const serviceLabels = useServiceLabel()
    // Convênio como pagador só existe na saúde. Profissional e parceiro só aparecem
    // com o recurso no menu: sem ele, a busca do seletor responde 403.
    const isHealth = user?.company?.segment === 'HEALTH'
    const { canSeeProfessionals, canSeePartners } = useEntityAccess()
    const originLabels = getOriginLabels(serviceLabels)
    const originArticles = getOriginArticles(serviceLabels)
    const navigate = useNavigate()
    const { id: paramId } = useParams<{ id: string }>()
    const [searchParams] = useSearchParams()
    // Como modal, o form é sempre criação e ignora a URL (o `:id`/query são da
    // página por trás, não da cobrança).
    const id = isModal ? undefined : paramId
    const isEditing = !!id
    const isFromSchedule = !isModal && searchParams.get('from') === 'schedule'
    // Cobrança originada de agendamento/orçamento (schedule OU budget=aprovação de orçamento):
    // parceiro, profissional, hospital e composição são HERDADOS do atendimento e ficam
    // bloqueados aqui — mudam nas telas de agendamento, não na cobrança.
    const isFromScheduling =
        isFromSchedule || searchParams.get('from') === 'budget'
    const isFromCounterSale =
        !isModal && searchParams.get('from') === 'counter-sale'
    const { autoSettle: offlineAutoSettle } = useOfflineSettlementPolicy()
    const [loading, setLoading] = useState(false)
    const [loadingData, setLoadingData] = useState(false)

    // Parcelamento próprio (entrada + N parcelas). Só na edição (a cobrança já existe).
    const [installOpen, setInstallOpen] = useState(false)
    const [installHasEntry, setInstallHasEntry] = useState(false)
    const [installEntryAmount, setInstallEntryAmount] = useState('')
    const [installEntryDate, setInstallEntryDate] = useState('')
    const [installCount, setInstallCount] = useState('2')
    const [installItems, setInstallItems] = useState<
        {
            amount: string
            dueDate: string
            paymentMethod: string
            manual?: boolean
        }[]
    >([])

    // Anexos: arquivos escolhidos que ainda não subiram. Na edição sobem na hora
    // (o billing já existe); na criação ficam aqui até o billing ganhar um id.
    const [pendingFiles, setPendingFiles] = useState<PendingAttachment[]>([])
    const [attachmentFile, setAttachmentFile] = useState<File | null>(null)
    const [attachmentDescription, setAttachmentDescription] = useState('')
    const [savingAttachment, setSavingAttachment] = useState(false)
    const fileInputRef = useRef<HTMLInputElement>(null)

    const [formData, setFormData] = useState<FormData>({
        ...initialFormData,
        ...(defaultType ? { type: defaultType } : {}),
    })

    /**
     * FORMAS DE PAGAMENTO ADICIONAIS — o cliente paga parte no cartão, parte em
     * dinheiro/PIX. O bloco "Valores e Vencimento" é a FORMA 1; cada forma extra é
     * uma cópia dele, com método, valor e VENCIMENTO próprios ("o dinheiro eu
     * trago semana que vem").
     *
     * NÃO é parcelamento: aquilo divide no TEMPO com controle de parcelas; aqui é
     * um pagamento só, dividido entre MEIOS. Ver docs/billing-orphan-and-splits.md.
     *
     * TOTAL CONSTANTE: o que se lança numa forma extra SAI do bloco principal —
     * o valor lá diminui na hora. Assim a soma nunca passa do que o cliente deve,
     * e o bloco principal continua sendo a forma 1 de verdade (não um campo morto).
     */
    const [splitItems, setSplitItems] = useState<
        { paymentMethod: string; amount: string; dueDate: string }[]
    >([])

    /**
     * Status da cobrança como veio do banco (só na edição). Cobrança ENCERRADA não
     * aceita split: repartir uma dívida que não existe mais criava filhas vivas — e
     * as offline nasciam PAGAS pela baixa automática — penduradas numa cobrança
     * morta. O backend recusa; aqui a ação nem aparece.
     */
    const [loadedBillingStatus, setLoadedBillingStatus] = useState<
        string | null
    >(null)
    const isClosedBilling = isClosedBillingStatus(loadedBillingStatus)
    /**
     * Cobrança que não aceita mais edição (paga, parcialmente paga, estornada,
     * cancelada, baixada). O botão "Editar" já some da lista e do detalhe, mas esta
     * tela é alcançável por URL direta e pelos atalhos do atendimento, que apontam
     * para `/edit` sem olhar o status — era por aí que se editava uma conta paga.
     * Aqui ela é fechada por inteiro, com o motivo na tela.
     */
    const isUneditableBilling =
        isEditing && !isEditableBillingStatus(loadedBillingStatus)

    /** Move `cents` do bloco principal para (ou de volta de) as formas extras. */
    const shiftFromMain = (cents: number) => {
        setFormData(prev => {
            const atual = parseCurrencyToInt(prev.amount)
            const novo = Math.max(0, atual - cents)
            return { ...prev, amount: formatCurrencyInput(String(novo)) }
        })
    }

    const addSplitItem = () =>
        setSplitItems(prev => [
            ...prev,
            { paymentMethod: '', amount: '', dueDate: formData.dueDate },
        ])

    const removeSplitItem = (index: number) => {
        // Devolve o valor da forma ao bloco principal.
        const devolver = parseCurrencyToInt(splitItems[index]?.amount ?? '')
        if (devolver > 0) shiftFromMain(-devolver)
        setSplitItems(prev => prev.filter((_, i) => i !== index))
    }

    const setSplitItemField = (
        index: number,
        field: 'paymentMethod' | 'amount' | 'dueDate',
        value: string
    ) => {
        if (field !== 'amount') {
            setSplitItems(prev =>
                prev.map((it, i) =>
                    i === index ? { ...it, [field]: value } : it
                )
            )
            return
        }
        const anterior = parseCurrencyToInt(splitItems[index]?.amount ?? '')
        const disponivel = parseCurrencyToInt(formData.amount) + anterior
        const pedido = parseCurrencyToInt(formatCurrencyInput(value))
        // Trava dura: uma forma nunca leva mais do que o principal tem a ceder.
        const final = Math.min(pedido, disponivel)
        shiftFromMain(final - anterior)
        setSplitItems(prev =>
            prev.map((it, i) =>
                i === index
                    ? { ...it, amount: formatCurrencyInput(String(final)) }
                    : it
            )
        )
    }

    /**
     * Métodos já ocupados por outra forma. Cada meio só pode aparecer UMA vez: duas
     * linhas "dinheiro" na mesma cobrança não descrevem nada que a maquininha ou o
     * extrato consigam distinguir depois — se o cliente pagou dois valores em
     * dinheiro, é uma forma só, com a soma.
     *
     * `exceptIndex` = a própria linha, que não conta como conflito consigo mesma;
     * `null` (ou omitido) considera todas as extras — é o caso do bloco principal.
     */
    const usedPaymentMethods = (exceptIndex?: number | null): string[] => {
        const extras = splitItems
            .filter((_, i) => i !== exceptIndex)
            .map(it => it.paymentMethod)
        return [
            ...(exceptIndex == null ? [] : [formData.paymentMethod]),
            ...extras,
        ].filter(Boolean)
    }

    /** Forma 1 (bloco principal) + extras, prontas para a API. */
    const buildSplitPayload = ():
        | {
              ok: true
              lines: {
                  paymentMethod: string
                  amount: number
                  dueDate?: string
              }[]
          }
        | { ok: false; error: string } => {
        if (splitItems.some(it => !it.paymentMethod)) {
            return {
                ok: false,
                error: 'Escolha o método de todas as formas de pagamento.',
            }
        }
        if (splitItems.some(it => parseCurrencyToInt(it.amount) <= 0)) {
            return {
                ok: false,
                error: 'Cada forma precisa de um valor maior que zero.',
            }
        }
        const principal = parseCurrencyToInt(formData.amount)
        if (principal <= 0) {
            return {
                ok: false,
                error: 'O valor do bloco principal ficou zerado. Reduza uma das formas adicionais.',
            }
        }
        if (!formData.paymentMethod) {
            return {
                ok: false,
                error: 'Escolha o método de pagamento do bloco principal.',
            }
        }
        // Rede de segurança: os selects já escondem o método ocupado, mas um estado
        // restaurado (rascunho, voltar do navegador) pode trazer repetição.
        const metodos = [
            formData.paymentMethod,
            ...splitItems.map(it => it.paymentMethod),
        ]
        if (new Set(metodos).size !== metodos.length) {
            return {
                ok: false,
                error: 'Cada forma de pagamento precisa de um método diferente.',
            }
        }
        return {
            ok: true,
            lines: [
                {
                    paymentMethod: formData.paymentMethod,
                    amount: principal,
                    dueDate: formData.dueDate || undefined,
                },
                ...splitItems.map(it => ({
                    paymentMethod: it.paymentMethod,
                    amount: parseCurrencyToInt(it.amount),
                    dueDate: it.dueDate || undefined,
                })),
            ],
        }
    }

    /**
     * Atendimento que originou esta cobrança (`billing.origin`, resolvido no
     * backend). Guardado para saber PARA ONDE voltar depois de salvar.
     */
    const [billingOrigin, setBillingOrigin] = useState<BillingOrigin | null>(
        null
    )

    // Schedule mode - nomes para exibição read-only
    const [schedulePartnerName, setSchedulePartnerName] = useState('')
    const [scheduleProfessionalName, setScheduleProfessionalName] = useState('')
    // Composição (snapshot) herdada do atendimento: materiais + itens de catálogo.
    const [billingComposition, setBillingComposition] = useState<{
        catalogItems: any[]
        medicalSupplies: any[]
    }>({ catalogItems: [], medicalSupplies: [] })
    const [maxDiscountPercent, setMaxDiscountPercent] = useState<number | null>(
        null
    )

    // Client search

    // Supplier search
    const [suppliers, setSuppliers] = useState<Supplier[]>([])
    const [filteredSuppliers, setFilteredSuppliers] = useState<Supplier[]>([])
    const [supplierSearch, setSupplierSearch] = useState('')
    const [showSupplierDropdown, setShowSupplierDropdown] = useState(false)
    const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(
        null
    )

    // Categorias, centros de custo, contas bancárias
    const [financialCategories, setFinancialCategories] = useState<
        FinancialCategoryItem[]
    >([])
    const [costCenters, setCostCenters] = useState<CostCenterItem[]>([])
    const [bankAccounts, setBankAccounts] = useState<BankAccountItem[]>([])

    // Available payment methods. Inicia conservador (sem os meios online),
    // que são liberados só após ler a política de checkout com sucesso.
    const [availableMethods, setAvailableMethods] =
        useState<string[]>(NON_GATED_METHODS)

    /**
     * Ainda sobra algum método para uma forma nova? Sem isto o operador adicionaria
     * um bloco cujo select nasce vazio e sem opção nenhuma — e o salvamento ficaria
     * travado por um campo que não há como preencher.
     *
     * Mora aqui, e não junto do resto do split lá em cima, porque depende de
     * `availableMethods`: avaliado antes desta linha, quebraria o render.
     */
    const hasSparePaymentMethod = PAYMENT_METHODS.some(
        m =>
            availableMethods.includes(m.value) &&
            m.value !== formData.paymentMethod &&
            !splitItems.some(it => it.paymentMethod === m.value)
    )

    // Terminals
    const [terminals, setTerminals] = useState<PaymentTerminal[]>([])
    /**
     * Taxa do método embutida no valor gravado da cobrança principal. A tela mostra
     * o valor do serviço (gravado menos esta taxa) e a devolve ao salvar.
     */
    const [existingFeeAmount, setExistingFeeAmount] = useState(0)
    const [selectedTerminalId, setSelectedTerminalId] = useState<number | null>(
        () => {
            const saved = localStorage.getItem('lastUsedTerminalId')
            return saved ? parseInt(saved, 10) : null
        }
    )

    const isPayable = formData.type === 'PAYABLE'

    /**
     * O lançamento foi criado pelo próprio sistema — repasse automático (`payoutKey`)
     * ou solicitação agrupada (`isPayoutBatch`). Nesses, a categoria é read-only.
     *
     * Não basta olhar a categoria: um repasse lançado à mão também é `PARTNER_PAYOUT`,
     * e o operador precisa poder corrigi-la se escolheu a errada.
     */
    const [isSystemGeneratedBilling, setIsSystemGeneratedBilling] =
        useState(false)

    /**
     * A categoria está sendo digitada, e não escolhida na lista.
     *
     * É estado, e não derivado de `formData.category`: assim que o operador escolhe
     * "Personalizado" o campo ainda está vazio, e um derivado devolveria o select para
     * "Sem categoria" antes de ele digitar a primeira letra.
     */
    const [isCustomCategory, setIsCustomCategory] = useState(false)

    /** As categorias oferecidas dependem de quem recebe (repasse exige destinatário compatível). */
    const categoryOptions = manualPayableCategoryOptions(
        formData.recipientType
    )
    const selectedCategoryHint = isCustomCategory
        ? null
        : (categoryOptions.find(o => o.value === formData.category)?.hint ??
          null)

    /**
     * Troca no select: "Personalizado" só liga o campo de texto (e limpa o código que
     * estava gravado, para não salvar `PARTNER_PAYOUT` como se fosse texto livre).
     */
    const handleCategoryOptionChange = (value: string) => {
        if (value === CUSTOM_CATEGORY_OPTION) {
            setIsCustomCategory(true)
            setFormData(prev => ({ ...prev, category: '' }))
            return
        }
        setIsCustomCategory(false)
        setFormData(prev => ({ ...prev, category: value }))
    }

    const [awaitingBatchSetup, setAwaitingBatchSetup] = useState(false)
    const [submittingBatch, setSubmittingBatch] = useState(false)
    /**
     * Os repasses que a solicitação agrupada soma.
     *
     * Carregados só enquanto ela está em configuração: é o momento em que o financeiro
     * confere a composição e pode tirar um atendimento que o parceiro ainda não
     * realizou. Depois de enviada, quem confere é o aprovador, na fila.
     */
    /**
     * A cobrança em edição é uma solicitação agrupada — inclusive depois de enviada
     * para a fila, ao contrário de `awaitingBatchSetup`. O valor dela é derivado dos
     * repasses e nunca digitado.
     */
    const [isPayoutBatchBilling, setIsPayoutBatchBilling] = useState(false)
    /**
     * O campo de valor fica fechado só na solicitação agrupada — e mesmo nela, apenas
     * para quem não pode editar cobranças.
     *
     * O valor de um lote é derivado dos repasses que ele soma, então o caminho normal
     * para mudá-lo continua sendo retirar um lançamento. Mas o financeiro precisa poder
     * corrigir o número quando o acerto com o destinatário não bate com a soma (glosa,
     * combinado fora do sistema): sem isso a única saída era recusar o lote e refazê-lo.
     * A permissão de update em `financial/billings` é o que separa quem pode assumir
     * essa divergência de quem só opera a tela.
     *
     * Restrito a PAYABLE: em cobrança a receber o valor nunca esteve fechado, e lote é
     * sempre conta a pagar — a condição existe para que a exceção não vaze para o
     * recebimento se um dia um RECEIVABLE for marcado como agrupado.
     */
    const { canUpdate: canUpdateBillings } = useResourcePermissions(
        'financial',
        'billings'
    )
    /**
     * Conta a RECEBER atrelada a um atendimento (ou pedido): o valor vem de lá e não se
     * digita aqui.
     *
     * Quem forma o preço é a cascata do catálogo no atendimento, onde o desconto tem
     * teto e o repasse ao parceiro é congelado. Mudando o número só na cobrança, o
     * cliente passa a dever um valor que não corresponde a serviço nenhum e o parceiro
     * continua recebendo pelo preço antigo — sem nada apontando a divergência.
     *
     * Só o VALOR fecha. Método, vencimento, desconto, observações e anexos seguem
     * editáveis de propósito: é exatamente para isso que o agendamento e a aprovação de
     * orçamento abrem esta tela (`?from=schedule`, `?from=budget`), e fechá-la inteira
     * deixaria o operador sem onde definir como e quando o paciente paga.
     *
     * `billingOrigin` vem resolvido do backend (`billing.origin`) e existe também em
     * conta a PAGAR (o repasse aponta para o atendimento que o originou), por isso o
     * `!isPayable`.
     */
    const isAppointmentReceivable = !isPayable && billingOrigin != null
    const isAmountReadOnly =
        (isPayoutBatchBilling && !(isPayable && canUpdateBillings)) ||
        isAppointmentReceivable
    /**
     * Totais da composição, vindos agregados do banco pela própria lista — a tela
     * conhece só a página aberta, então contar o que está montado anunciaria vinte
     * lançamentos de uma solicitação com mil.
     */
    const [batchSummary, setBatchSummary] = useState({ total: 0, totalAmount: 0 })
    const handleBatchSummary = useCallback(
        (next: { total: number; totalAmount: number }) => setBatchSummary(next),
        []
    )
    /** Trocar este número recarrega a lista — usado depois de retirar um lançamento. */
    const [batchReloadToken, setBatchReloadToken] = useState(0)
    const [removingItemId, setRemovingItemId] = useState<number | null>(null)
    const [itemToRemove, setItemToRemove] = useState<number | null>(null)

    /**
     * Tira um lançamento da solicitação.
     *
     * O valor da conta muda no servidor, então o campo da tela é atualizado junto —
     * senão o próximo "Salvar" gravaria de volta o total de antes.
     */
    const handleRemoveBatchItem = async (billingId: number) => {
        const batchId = id ? parseInt(id, 10) : NaN
        if (!Number.isFinite(batchId)) return
        setRemovingItemId(billingId)
        try {
            const res = await removePayoutBatchItem(batchId, billingId)
            if (res.success && res.result) {
                setBatchReloadToken(token => token + 1)
                setFormData(prev => ({
                    ...prev,
                    amount: intToCurrencyDisplay(res.result!.remainingAmount),
                }))
                toast.success(
                    'Lançamento retirado. Ele continua em aberto e volta no próximo agrupamento.'
                )
            } else {
                toast.error(res.error || 'Não foi possível retirar o lançamento')
            }
        } catch (err: any) {
            toast.error(
                err?.response?.data?.message ||
                    'Não foi possível retirar o lançamento'
            )
        } finally {
            setRemovingItemId(null)
            setItemToRemove(null)
        }
    }

    /**
     * Marca que o envio para aprovação deve acontecer DEPOIS de o formulário gravar.
     *
     * O botão antes chamava `submitPayoutBatch` direto, sem salvar — o comentário aqui
     * até dizia "salva o que está na tela", mas nada salvava. O vencimento e as formas
     * que o financeiro acabou de montar ficavam só na tela: o lote ia à fila com o
     * valor cheio, sem filhas, e o demonstrativo era gerado nesse estado. Na aprovação
     * saía a transferência do total, enquanto parte estava combinada em dinheiro.
     *
     * Um ref, e não um estado: o `handleSubmit` lê o valor durante a própria execução,
     * e um `useState` só estaria atualizado no render seguinte.
     */
    const submitBatchAfterSaveRef = useRef(false)
    const formRef = useRef<HTMLFormElement>(null)

    /** Fim do caminho do financeiro: grava o que está na tela e manda para a fila. */
    const handleSubmitBatchForApproval = () => {
        if (!id) return
        submitBatchAfterSaveRef.current = true
        // `requestSubmit` (e não `handleSubmit` direto) para o formulário passar pela
        // validação nativa antes — vencimento em branco tem de barrar aqui, não no
        // meio do envio.
        formRef.current?.requestSubmit()
    }

    /**
     * Depois de gravar: envia para a fila, se foi isso que o operador pediu.
     *
     * Devolve `true` quando assumiu a navegação, para o `handleSubmit` não navegar
     * também.
     */
    const finishBatchSubmission = async (): Promise<boolean> => {
        if (!submitBatchAfterSaveRef.current || !id) return false
        submitBatchAfterSaveRef.current = false
        setSubmittingBatch(true)
        try {
            const res = await submitPayoutBatch(parseInt(id, 10))
            if (res.success) {
                toast.success(
                    'Solicitação enviada para aprovação com o demonstrativo anexado.'
                )
                navigate('/financial/bills-approvals')
            } else {
                toast.error(res.error || 'Erro ao enviar para aprovação')
                // Fica na tela: o que foi digitado já está salvo, e o operador pode
                // tentar de novo sem remontar nada.
            }
        } catch {
            toast.error('Erro ao enviar para aprovação')
        } finally {
            setSubmittingBatch(false)
        }
        return true
    }
    const isReceivable = formData.type === 'RECEIVABLE'

    /**
     * "Pago em" só aparece quando salvar vai DAR BAIXA: conta a receber do próprio
     * cliente, política de baixa automática ligada e alguma forma recebida por fora
     * (dinheiro, maquininha, "outro") — na principal ou numa forma extra. Fora disso a
     * data não teria efeito nenhum, e um campo que não faz nada engana.
     */
    const showPaidAt =
        isReceivable &&
        offlineAutoSettle &&
        formData.payerType !== 'HEALTH_PLAN' &&
        [formData.paymentMethod, ...splitItems.map(it => it.paymentMethod)].some(
            method => OFFLINE_SETTLEMENT_METHODS.includes(method)
        )
    const effectivePaidAt = formData.paidAt || localTodayInput()

    useEffect(() => {
        if (user?.companyId) {
            loadSuppliers()
            loadAvailablePaymentMethods()
            loadTerminals()
            getFinancialCategories().then(r => {
                if (r.success) setFinancialCategories(r.result || [])
            })
            getCostCenters().then(r => {
                if (r.success) setCostCenters(r.result || [])
            })
            getBankAccounts().then(r => {
                if (r.success) setBankAccounts(r.result || [])
            })
        }
    }, [user?.companyId])

    // Refetch métodos de pagamento ao retomar foco da janela (cobre cenário onde
    // o user alterou gateway em outra aba e voltou pro form sem desmontá-lo)
    useEffect(() => {
        if (!user?.companyId) return
        const onFocus = () => {
            loadAvailablePaymentMethods()
            loadTerminals()
        }
        window.addEventListener('focus', onFocus)
        return () => window.removeEventListener('focus', onFocus)
    }, [user?.companyId])

    useEffect(() => {
        if (isEditing && id) {
            loadBilling()
        }
    }, [id, isEditing])

    useEffect(() => {
        if (supplierSearch.trim()) {
            const term = supplierSearch.toLowerCase()
            setFilteredSuppliers(
                suppliers.filter(
                    s =>
                        s.name?.toLowerCase().includes(term) ||
                        s.tradingName?.toLowerCase().includes(term) ||
                        s.cnpj?.includes(supplierSearch)
                )
            )
        } else {
            setFilteredSuppliers(suppliers)
        }
    }, [supplierSearch, suppliers])

    const loadSuppliers = async () => {
        if (!user?.companyId) return
        try {
            const response = await getSuppliers(user.companyId)
            if (response.success) {
                const all = Array.isArray(response.result)
                    ? response.result
                    : (response.result as any)?.data || []
                setSuppliers(all)
                setFilteredSuppliers(all)
            }
        } catch (error) {
            console.error('Erro ao carregar fornecedores:', error)
        }
    }

    const loadAvailablePaymentMethods = async () => {
        // Tenta algumas vezes antes de degradar. A falha aqui é transitória, não
        // "meio desabilitado": logo após um login novo, a PRIMEIRA leitura desta
        // política pode voltar 403 enquanto o cache de permissões do backend
        // ainda aquece — e o modal (PDV rápido) é aberto direto da sidebar, sem
        // antes passar por nenhuma tela financeira que aqueceria esse cache. O
        // sintoma era abrir o modal e ver só os 3 meios não-online; um refresh
        // "consertava" porque a releitura já pegava o cache quente. Sem o retry,
        // a degradação de segurança (fail-closed) virava um bug de UX.
        for (let attempt = 0; attempt < 3; attempt++) {
            try {
                const res = await getCheckoutPaymentPolicy()
                if (res.success && res.result?.methods) {
                    // Gate apenas os meios online principais; demais (dinheiro,
                    // boleto, cheque, terminal etc.) seguem sempre disponíveis.
                    const online: Record<string, boolean> = {
                        PIX: res.result.methods.pix,
                        CREDIT_CARD: res.result.methods.creditCard,
                        DEBIT_CARD: res.result.methods.debitCard,
                    }
                    setAvailableMethods(
                        DEFAULT_PAYMENT_METHODS.filter(
                            method => !(method in online) || online[method]
                        )
                    )
                    return
                }
            } catch {
                // erro ao ler a política (ex.: 403 por falta de acesso às
                // Configurações Financeiras, ou cache de permissão frio) —
                // tenta de novo; se persistir, fail-closed abaixo.
            }
            await new Promise(resolve => setTimeout(resolve, 400))
        }
        // Persistiu falhando: NÃO ofertar os meios online (não sabemos se estão
        // habilitados). Mantém apenas os meios que não dependem da configuração
        // de gateway. Fail-closed — nunca fail-open, que mascararia meios
        // desativados. Se o usuário realmente não tem acesso, para aqui.
        setAvailableMethods(NON_GATED_METHODS)
    }

    const loadTerminals = async () => {
        try {
            const res = await getTerminals()
            if (res.success && res.result) {
                const active = res.result.filter(t => t.active)
                setTerminals(active)
                if (
                    selectedTerminalId &&
                    !active.find(t => t.id === selectedTerminalId)
                ) {
                    setSelectedTerminalId(null)
                    localStorage.removeItem('lastUsedTerminalId')
                }
            }
        } catch {
            // Silently fail
        }
    }

    const loadBilling = async () => {
        if (!id) return

        setLoadingData(true)
        try {
            const response = await getBilling(parseInt(id, 10))
            if (response.success && response.result) {
                const b = response.result
                setBillingOrigin(((b as any).origin as BillingOrigin) || null)
                setLoadedBillingStatus((b as any).status || null)
                // Gerado pelo sistema: repasse automático tem `payoutKey`, a
                // solicitação agrupada tem `isPayoutBatch`. Nos dois a categoria é o
                // que move dinheiro, e não se edita.
                setIsSystemGeneratedBilling(
                    !!(b as any).payoutKey || (b as any).isPayoutBatch === true
                )
                // Categoria fora da lista (texto digitado antes de o select existir):
                // abre já no modo personalizado, com o texto preservado.
                setIsCustomCategory(
                    !!b.category &&
                        !MANUAL_PAYABLE_CATEGORY_OPTIONS.some(
                            o => o.value === b.category
                        )
                )
                setFormData({
                    type: b.type || 'RECEIVABLE',
                    clientId: b.clientId,
                    payerType: b.payerType || 'CLIENT',
                    healthPlanId: b.healthPlanId ?? null,
                    partnerId: b.partnerId,
                    professionalId: b.professionalId,
                    supplierId: b.supplierId || null,
                    supplierName: b.supplierName || '',
                    hospitalId: b.hospitalId || null,
                    userId: (b as any).userId || null,
                    employeeId: (b as any).employeeId || null,
                    recipientType:
                        b.supplierId || b.supplierName
                            ? 'SUPPLIER'
                            : b.clientId
                              ? 'CLIENT'
                              : b.partnerId
                                ? 'PARTNER'
                                : b.professionalId
                                  ? 'PROFESSIONAL'
                                  : b.hospitalId
                                    ? 'HOSPITAL'
                                    : (b as any).userId
                                      ? 'USER'
                                      : (b as any).employeeId
                                        ? 'EMPLOYEE'
                                        : 'SUPPLIER',
                    category: b.category || '',
                    financialCategoryId: b.financialCategoryId || null,
                    costCenterId: b.costCenterId || null,
                    bankAccountId: b.bankAccountId || null,
                    description: b.description || '',
                    referenceCode: b.referenceCode || '',
                    // Valor do SERVIÇO, sem a taxa do método: é o que se digita na
                    // criação e o que as formas de pagamento repartem. A taxa volta
                    // a ser somada no save (ver `amount` do payload).
                    amount: b.amount
                        ? intToCurrencyDisplay(
                              b.amount - ((b as any).feeAmount ?? 0)
                          )
                        : '',
                    discount: b.discount
                        ? intToCurrencyDisplay(b.discount)
                        : '',
                    interest: b.interest
                        ? intToCurrencyDisplay(b.interest)
                        : '',
                    dueDate: b.dueDate ? b.dueDate.split('T')[0] : '',
                    // Venda de balcão chega aqui em aberto, com o vencimento no DIA DA
                    // VENDA. Registrada depois, o dinheiro entrou naquele dia — não hoje.
                    paidAt:
                        isFromCounterSale &&
                        b.dueDate &&
                        b.dueDate.split('T')[0] <= localTodayInput()
                            ? b.dueDate.split('T')[0]
                            : '',
                    paymentMethod: b.paymentMethod || '',
                    installmentCount: (b as any).installmentCount ?? null,
                    installmentNumber: b.installmentNumber?.toString() || '',
                    installmentTotal: b.installmentTotal?.toString() || '',
                    pixKey: b.pixKey || '',
                    pixQrCode: b.pixQrCode || '',
                    pixCopyPaste: b.pixCopyPaste || '',
                    pixExpirationDate: b.pixExpirationDate
                        ? b.pixExpirationDate.split('T')[0]
                        : '',
                    bankSlipUrl: b.bankSlipUrl || '',
                    nossoNumero: b.nossoNumero || '',
                    barcode: b.barcode || '',
                    digitableLine: b.digitableLine || '',
                    notes: b.notes || '',
                    attachments: (b.attachments || []).map(a => ({
                        url: a.url,
                        name: a.name,
                        description: a.description || '',
                    })),
                    enableRecurring: false,
                    recurringDueDay: 5,
                    terminalInstallments: '1',
                    recurringInterval: 'MONTHLY',
                    recurringMaxOccurrences: '',
                })
                setExistingFeeAmount((b as any).feeAmount ?? 0)
                // Solicitação de repasse agrupado que ainda não foi à fila: o
                // financeiro está aqui justamente para configurá-la, e o botão de
                // enviar para aprovação é o fim desse caminho.
                setIsPayoutBatchBilling((b as any).isPayoutBatch === true)

                // Formas de pagamento já gravadas: sem hidratar, o operador reabria a
                // edição e via só a fatia da linha principal (R$ 141,44) enquanto a
                // tela de detalhes somava tudo (R$ 191,44) — dois números discordando
                // na cara dele. Pior: salvar de novo mandaria uma lista de formas
                // vazia.
                const existingSplits = (((b as any).adjustments ?? []) as any[])
                    .filter(
                        child =>
                            child?.metadata?.paymentSplit === true &&
                            child?.status !== 'CANCELLED',
                    )
                    .map(child => ({
                        paymentMethod: child.paymentMethod ?? '',
                        amount: intToCurrencyDisplay(
                            (child.amount ?? 0) - (child.feeAmount ?? 0)
                        ),
                        dueDate: child.dueDate
                            ? String(child.dueDate).split('T')[0]
                            : '',
                    }))
                setSplitItems(existingSplits)
                setAwaitingBatchSetup(
                    (b as any).isPayoutBatch === true &&
                        (b as any).metadata?.awaitingSetup === true
                )
                if ((b as any).metadata?.maxDiscount != null) {
                    setMaxDiscountPercent((b as any).metadata.maxDiscount)
                }
                if (b.partner) {
                    setSchedulePartnerName(
                        (b.partner as any)?.tradingName ||
                            (b.partner as any)?.name ||
                            ''
                    )
                }
                if (b.professional) {
                    setScheduleProfessionalName(
                        (b.professional as any)?.name || ''
                    )
                }
                setBillingComposition({
                    catalogItems: (b as any).catalogItems || [],
                    medicalSupplies: (b as any).medicalSupplies || [],
                })
                if (b.supplier) {
                    setSelectedSupplier(b.supplier as any)
                }
            } else {
                toast.error('Cobrança não encontrada')
                navigate('/financial/billings')
            }
        } catch (error) {
            toast.error('Erro ao carregar cobrança')
            navigate('/financial/billings')
        } finally {
            setLoadingData(false)
        }
    }

    const handleChange = (
        e: React.ChangeEvent<
            HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
        >
    ) => {
        const { name, value } = e.target
        setFormData(prev => ({ ...prev, [name]: value }))
    }

    const handleCurrencyChange =
        (field: 'amount' | 'discount' | 'interest') =>
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setFormData(prev => ({
                ...prev,
                [field]: formatCurrencyInput(e.target.value),
            }))
        }

    // --- Parcelamento próprio ---
    const addMonthsIso = (isoDate: string, n: number): string => {
        const [y, m, d] = (isoDate || '').split('-').map(Number)
        if (!y || !m || !d) return isoDate
        const dt = new Date(y, m - 1 + n, d)
        const mm = String(dt.getMonth() + 1).padStart(2, '0')
        const dd = String(dt.getDate()).padStart(2, '0')
        return `${dt.getFullYear()}-${mm}-${dd}`
    }

    const generateInstallments = () => {
        const total = parseCurrencyToInt(formData.amount)
        const entry = installHasEntry
            ? parseCurrencyToInt(installEntryAmount)
            : 0
        const count = Math.max(1, parseInt(installCount, 10) || 1)
        const remaining = Math.max(0, total - entry)
        const base = Math.floor(remaining / count)
        // ajuste de arredondamento vai na 1ª parcela
        const first = remaining - base * (count - 1)
        const baseDate =
            formData.dueDate || new Date().toISOString().split('T')[0]
        const offset = installHasEntry ? 1 : 0
        const items = Array.from({ length: count }, (_, i) => ({
            amount: intToCurrencyDisplay(i === 0 ? first : base),
            dueDate: addMonthsIso(baseDate, offset + i),
            paymentMethod: formData.paymentMethod || '',
            manual: false,
        }))
        setInstallItems(items)
    }

    // Alvo das parcelas = total da cobrança − entrada.
    const installmentsTarget = (): number => {
        const total = parseCurrencyToInt(formData.amount)
        const entry = installHasEntry
            ? parseCurrencyToInt(installEntryAmount)
            : 0
        return Math.max(0, total - entry)
    }

    // Redistribui o restante (alvo − parcelas editadas à mão) igualmente entre as
    // parcelas NÃO editadas manualmente. O arredondamento vai na 1ª parcela automática.
    const rebalanceInstallments = (
        items: typeof installItems
    ): typeof installItems => {
        const target = installmentsTarget()
        const manualSum = items
            .filter(it => it.manual)
            .reduce((s, it) => s + parseCurrencyToInt(it.amount), 0)
        const autoCount = items.filter(it => !it.manual).length
        if (autoCount === 0) return items
        const remaining = Math.max(0, target - manualSum)
        const base = Math.floor(remaining / autoCount)
        const first = remaining - base * (autoCount - 1)
        let autoIdx = 0
        return items.map(it => {
            if (it.manual) return it
            const val = autoIdx === 0 ? first : base
            autoIdx++
            return { ...it, amount: intToCurrencyDisplay(val) }
        })
    }

    // Ao mudar entrada / total da cobrança, rebalanceia as parcelas automáticas.
    useEffect(() => {
        setInstallItems(prev =>
            prev.length > 0 ? rebalanceInstallments(prev) : prev
        )
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [installEntryAmount, installHasEntry, formData.amount])

    // Editar o valor de uma parcela: fixa ela (manual) e redistribui o restante
    // entre as demais parcelas automáticas.
    const handleInstallAmountChange = (index: number, raw: string) => {
        setInstallItems(prev =>
            rebalanceInstallments(
                prev.map((p, idx) =>
                    idx === index
                        ? {
                              ...p,
                              amount: formatCurrencyInput(raw),
                              manual: true,
                          }
                        : p
                )
            )
        )
    }

    // O parcelamento só vale para cobrança do próprio cliente: convênio paga por
    // fatura, e não recebe PIX/boleto nem link.
    const installAllowed = !isPayable && formData.payerType !== 'HEALTH_PLAN'
    const installActive =
        installAllowed && installOpen && installItems.length > 0

    // Monta e valida o plano ANTES de salvar a cobrança: falhar só depois deixava a
    // cobrança gravada sem o parcelamento (e, na criação, um novo "Salvar" criava
    // outra).
    const buildInstallmentPlan = ():
        | { ok: true; plan: CreateInstallmentPlanData }
        | { ok: false; error: string } => {
        const items = installItems.map(it => ({
            amount: parseCurrencyToInt(it.amount),
            dueDate: it.dueDate,
            paymentMethod: it.paymentMethod || undefined,
        }))
        if (
            items.length < 1 ||
            items.some(it => it.amount <= 0 || !it.dueDate)
        ) {
            return { ok: false, error: 'Preencha valor e data de todas as parcelas' }
        }
        if (items.some(it => !it.paymentMethod)) {
            return {
                ok: false,
                error: 'Escolha o método de pagamento de todas as parcelas',
            }
        }
        const downPayment = installHasEntry
            ? {
                  amount: parseCurrencyToInt(installEntryAmount),
                  dueDate: installEntryDate || formData.dueDate,
              }
            : undefined
        if (downPayment && (downPayment.amount <= 0 || !downPayment.dueDate)) {
            return { ok: false, error: 'Preencha valor e data da entrada' }
        }
        const planTotal =
            (downPayment?.amount ?? 0) +
            items.reduce((sum, it) => sum + it.amount, 0)
        const billingTotal = parseCurrencyToInt(formData.amount)
        if (planTotal !== billingTotal) {
            return {
                ok: false,
                error: `A soma da entrada e das parcelas (${intToCurrencyDisplay(planTotal)}) não bate com o valor da cobrança (${intToCurrencyDisplay(billingTotal)})`,
            }
        }
        return {
            ok: true,
            plan: {
                downPayment,
                installments: items,
                paymentMethod: formData.paymentMethod || undefined,
            },
        }
    }

    // Escolhe o método de uma parcela e preenche as demais que estiverem SEM método
    // com o mesmo valor (ajuda o operador a não escolher uma por uma).
    const setInstallItemMethod = (index: number, method: string) => {
        setInstallItems(prev =>
            prev.map((p, i) =>
                i === index
                    ? { ...p, paymentMethod: method }
                    : p.paymentMethod
                      ? p
                      : { ...p, paymentMethod: method }
            )
        )
    }

    // O `ClientSelector` guarda o cliente escolhido e o exibe; aqui só interessa o id.
    const handleSelectClient = (client: Client) => {
        setFormData(prev => ({ ...prev, clientId: client.id }))
    }

    const handleClearClient = () => {
        setFormData(prev => ({ ...prev, clientId: null }))
    }

    const handleSelectSupplier = (supplier: Supplier) => {
        setSelectedSupplier(supplier)
        setFormData(prev => ({
            ...prev,
            supplierId: supplier.id,
            supplierName: '',
        }))
        setShowSupplierDropdown(false)
        setSupplierSearch('')
    }

    const handleClearSupplier = () => {
        setSelectedSupplier(null)
        setFormData(prev => ({ ...prev, supplierId: null }))
        setSupplierSearch('')
    }

    const handleSelectType = (type: BillingType) => {
        setFormData(prev => ({ ...prev, type }))
    }

    const clearAttachmentFields = () => {
        setAttachmentFile(null)
        setAttachmentDescription('')
        if (fileInputRef.current) fileInputRef.current.value = ''
    }

    /**
     * Na edição o billing já existe, então o arquivo sobe na hora — mesmo fluxo da
     * tela de detalhes. Na criação ainda não há id para o endpoint
     * `POST /billings/:id/attachments`, então o arquivo fica pendente e sobe depois
     * que o billing é criado (ver `uploadPendingAttachments`).
     */
    const handleAddAttachment = async () => {
        if (!attachmentFile) return

        const description = attachmentDescription.trim()

        if (!isEditing) {
            setPendingFiles(prev => [
                ...prev,
                { file: attachmentFile, description },
            ])
            clearAttachmentFields()
            return
        }

        setSavingAttachment(true)
        try {
            const response = await uploadBillingAttachment(
                parseInt(id!, 10),
                attachmentFile,
                description || undefined
            )
            if (response.success) {
                toast.success('Anexo adicionado')
                setFormData(prev => ({
                    ...prev,
                    attachments: (response.result?.attachments ||
                        []) as AttachmentItem[],
                }))
                clearAttachmentFields()
            } else {
                toast.error(response.error || 'Erro ao adicionar anexo')
            }
        } catch {
            toast.error('Erro ao adicionar anexo')
        } finally {
            setSavingAttachment(false)
        }
    }

    /** Remove um anexo já persistido. Na edição, remove também no backend. */
    const handleRemoveAttachment = async (index: number) => {
        if (!isEditing) {
            setFormData(prev => ({
                ...prev,
                attachments: prev.attachments.filter((_, i) => i !== index),
            }))
            return
        }

        setSavingAttachment(true)
        try {
            const response = await removeBillingAttachment(
                parseInt(id!, 10),
                index
            )
            if (response.success) {
                toast.success('Anexo removido')
                setFormData(prev => ({
                    ...prev,
                    attachments: prev.attachments.filter((_, i) => i !== index),
                }))
            } else {
                toast.error(response.error || 'Erro ao remover anexo')
            }
        } catch {
            toast.error('Erro ao remover anexo')
        } finally {
            setSavingAttachment(false)
        }
    }

    const handleRemovePendingFile = (index: number) => {
        setPendingFiles(prev => prev.filter((_, i) => i !== index))
    }

    /**
     * Anexos a enviar: os já adicionados à lista mais o arquivo que está no seletor
     * e ainda não foi adicionado. Sem isso, escolher um arquivo e clicar direto em
     * "Salvar" (sem passar por "Adicionar anexo") descartaria o arquivo em silêncio.
     */
    const collectAttachmentsToUpload = (): PendingAttachment[] => {
        const list = [...pendingFiles]
        if (attachmentFile) {
            list.push({
                file: attachmentFile,
                description: attachmentDescription.trim(),
            })
        }
        return list
    }

    /**
     * Sobe os arquivos pendentes após a criação do billing. Uma falha aqui não
     * invalida o billing já criado — avisa e segue, para o usuário reenviar pela
     * tela de detalhes.
     */
    const uploadPendingAttachments = async (
        billingId: number,
        files: PendingAttachment[]
    ) => {
        const errors: string[] = []
        for (const pending of files) {
            try {
                const response = await uploadBillingAttachment(
                    billingId,
                    pending.file,
                    pending.description || undefined
                )
                if (!response.success) {
                    errors.push(
                        `${pending.file.name}: ${response.error || 'falha no envio'}`
                    )
                }
            } catch (error) {
                errors.push(
                    `${pending.file.name}: ${(error as Error)?.message || 'falha no envio'}`
                )
            }
        }
        if (errors.length) {
            toast.warning(
                `${errors.length} de ${files.length} anexo(s) não foram enviados. Adicione-os pela tela de detalhes.`
            )
            errors.forEach(message => toast.error(message))
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()

        if (!user?.companyId) {
            toast.error('Usuário não autenticado')
            return
        }

        if (!formData.type) {
            toast.warning('Selecione o tipo do lançamento')
            return
        }

        if (isReceivable && !formData.clientId) {
            toast.warning('Selecione um cliente')
            return
        }

        if (isPayable && !hasPayableRecipient()) {
            toast.warning('Selecione ou informe um destinatário')
            return
        }

        const amountCents = parseCurrencyToInt(formData.amount)
        if (amountCents <= 0) {
            toast.warning('Informe um valor válido')
            return
        }

        if (!formData.dueDate) {
            toast.warning('Informe a data de vencimento')
            return
        }

        if (showPaidAt && effectivePaidAt > localTodayInput()) {
            toast.warning('A data de pagamento não pode ser futura')
            return
        }

        let installmentPlan: CreateInstallmentPlanData | undefined
        if (installActive) {
            if (splitItems.length > 0) {
                toast.warning(
                    'Cobrança dividida em formas de pagamento não pode ser parcelada. Remova as formas extras ou desligue o parcelamento.'
                )
                return
            }
            const built = buildInstallmentPlan()
            if (!built.ok) {
                toast.warning(built.error)
                return
            }
            installmentPlan = built.plan
        }

        setLoading(true)

        try {
            const dataToSend: any = {
                type: formData.type,
                companyId: user.companyId,
                // Na edição a tela mostra o valor sem a taxa; o backend grava o
                // total com ela. Sem devolvê-la aqui, salvar sem mexer no valor
                // apagaria a taxa — e, em cobrança de atendimento, seria recusado
                // como mudança de valor.
                amount: isEditing ? amountCents + existingFeeAmount : amountCents,
                dueDate: formData.dueDate,
            }

            if (showPaidAt) dataToSend.paidAt = effectivePaidAt

            if (isReceivable) {
                if (formData.clientId) dataToSend.clientId = formData.clientId
                // Pagador convênio: o paciente segue em clientId (é dele o
                // atendimento) e o convênio é quem deve. O backend recusa
                // healthPlanId sem payerType = HEALTH_PLAN, então os dois andam
                // sempre juntos.
                if (formData.payerType === 'HEALTH_PLAN' && formData.healthPlanId) {
                    dataToSend.payerType = 'HEALTH_PLAN'
                    dataToSend.healthPlanId = formData.healthPlanId
                }
            }

            if (isPayable) {
                // Persiste apenas a FK do tipo selecionado, conforme picker
                const t = formData.recipientType
                if (t === 'SUPPLIER') {
                    if (formData.supplierId)
                        dataToSend.supplierId = formData.supplierId
                    if (formData.supplierName.trim())
                        dataToSend.supplierName = formData.supplierName.trim()
                }
                if (t === 'CLIENT' && formData.clientId)
                    dataToSend.clientId = formData.clientId
                if (t === 'PARTNER' && formData.partnerId)
                    dataToSend.partnerId = formData.partnerId
                if (t === 'PROFESSIONAL' && formData.professionalId)
                    dataToSend.professionalId = Number(formData.professionalId)
                if (t === 'HOSPITAL' && formData.hospitalId)
                    dataToSend.hospitalId = formData.hospitalId
                if (t === 'USER' && formData.userId)
                    dataToSend.userId = formData.userId
                if (t === 'EMPLOYEE' && formData.employeeId)
                    dataToSend.employeeId = formData.employeeId
                if (formData.category.trim())
                    dataToSend.category = formData.category.trim()
            }

            if (!isPayable) {
                if (formData.partnerId)
                    dataToSend.partnerId = formData.partnerId
                if (formData.professionalId)
                    dataToSend.professionalId = Number(formData.professionalId)
            }
            if (formData.financialCategoryId)
                dataToSend.financialCategoryId = formData.financialCategoryId
            if (formData.costCenterId)
                dataToSend.costCenterId = formData.costCenterId
            if (formData.bankAccountId)
                dataToSend.bankAccountId = formData.bankAccountId
            if (formData.description.trim())
                dataToSend.description = formData.description.trim()
            if (formData.referenceCode.trim())
                dataToSend.referenceCode = formData.referenceCode.trim()
            if (formData.discount)
                dataToSend.discount = parseCurrencyToInt(formData.discount)
            if (formData.interest)
                dataToSend.interest = parseCurrencyToInt(formData.interest)
            if (formData.paymentMethod)
                dataToSend.paymentMethod = formData.paymentMethod
            // Sempre enviado (inclusive `null`): é assim que trocar de parcelado para
            // à vista limpa o combinado — omitir manteria o número antigo travando o
            // checkout num parcelamento que ninguém mais quer.
            if (isReceivable)
                dataToSend.installmentCount = formData.installmentCount
            if (selectedTerminalId) dataToSend.terminalId = selectedTerminalId
            if (formData.installmentNumber)
                dataToSend.installmentNumber = parseInt(
                    formData.installmentNumber,
                    10
                )
            if (formData.installmentTotal)
                dataToSend.installmentTotal = parseInt(
                    formData.installmentTotal,
                    10
                )
            if (formData.notes.trim()) dataToSend.notes = formData.notes.trim()

            // Anexos não vão no payload: são enviados pelo endpoint de upload
            // (`POST /billings/:id/attachments`), que exige um billing existente.

            // Recorrência
            if (formData.enableRecurring && !isEditing) {
                dataToSend.enableRecurring = true
                dataToSend.recurringDueDay = formData.recurringDueDay
            }

            /**
             * Para onde ir depois de salvar.
             *
             * Cobrança aberta a partir de um atendimento (`from=schedule` ao
             * agendar, `from=budget` ao aprovar orçamento) volta para o
             * ATENDIMENTO, não para o detalhe da cobrança: o operador estava
             * agendando, e o acerto do valor é uma etapa do meio do caminho —
             * largá-lo no financeiro obriga a navegar de volta na mão.
             *
             * Sem origem de atendimento (cobrança avulsa, pedido da loja), o
             * destino continua sendo o detalhe da cobrança.
             */
            const returnPath = (savedId?: number | null): string => {
                if (isFromScheduling) {
                    const originPath = billingOriginPath(billingOrigin)
                    if (originPath && isAppointmentOrigin(billingOrigin)) {
                        return originPath
                    }
                }
                const target = isEditing ? id : savedId
                return target
                    ? `/financial/billings/${target}`
                    : '/financial/billings'
            }

            // Split na CRIAÇÃO: viaja no payload. O backend escolhe a linha de
            // gateway como principal (é ela que vai ao Asaas, só com o valor
            // dela) e cria as demais como filhas, que herdam a baixa automática
            // do offline. Na edição o caminho é o endpoint próprio (acima).
            let splitPayload:
                | { paymentMethod: string; amount: number; dueDate?: string }[]
                | undefined
            // Vale para os dois tipos: o cliente que paga parte no cartão e parte em
            // dinheiro, e o repasse que a clínica paga parte por PIX e parte em
            // espécie. O backend trata a diferença — nas formas de um repasse
            // agrupado a despesa não é relançada e quem vai à fila é o agrupador.
            if (splitItems.length > 0) {
                const built = buildSplitPayload()
                if (!built.ok) {
                    toast.error(built.error)
                    setLoading(false)
                    return
                }
                // `formData.amount` é a fatia da forma 1 — o valor das extras já saiu
                // dele.
                //
                // Na CRIAÇÃO o que se grava é o TOTAL: o backend é quem depois reduz a
                // principal para a fatia da linha de gateway.
                //
                // Na EDIÇÃO o valor NÃO viaja aqui. Quem redistribui é o endpoint de
                // formas, e a validação dele soma a principal com as filhas ainda
                // vivas para chegar ao total atual — mandar o total no `update` fazia a
                // principal já valer o total, a conta virava "total + filhas antigas" e
                // toda edição de cobrança dividida era recusada com "a soma das formas
                // não bate". A tela dizia "atualizada" para os outros campos e as
                // formas ficavam como estavam.
                if (isEditing) {
                    delete dataToSend.amount
                } else {
                    dataToSend.amount = built.lines.reduce(
                        (soma, linha) => soma + linha.amount,
                        0
                    )
                    splitPayload = built.lines
                }
            }

            // Vai parcelar em seguida: o backend não gera PIX/boleto nem manda o
            // link do valor cheio — a entrada é cobrada pelo plano.
            //
            // Mesma coisa ao dividir na EDIÇÃO com Pix ou boleto na forma 1: o PATCH
            // geraria o Pix pelo total e o PUT das formas, logo abaixo, seria recusado
            // ("já tem pagamento gerado no gateway"). É o PUT que gera o Pix da fatia.
            const splitDefersGateway =
                isEditing &&
                splitItems.length > 0 &&
                ['PIX', 'BOLETO'].includes(formData.paymentMethod)
            const gatewayOptions = { deferGateway: !!installmentPlan || splitDefersGateway }
            let response
            if (isEditing) {
                response = await updateBilling(
                    parseInt(id!, 10),
                    dataToSend,
                    gatewayOptions
                )
            } else {
                response = await createBilling(
                    splitPayload
                        ? ({
                              ...dataToSend,
                              paymentSplits: splitPayload,
                          } as any)
                        : dataToSend,
                    gatewayOptions
                )
            }

            const label = isPayable ? 'Conta a pagar' : 'Cobrança'

            if (response.success) {
                // Só agora existe um id para o endpoint de anexos. Na edição, o
                // arquivo ainda no seletor (não adicionado) também precisa subir.
                const filesToUpload = collectAttachmentsToUpload()
                const billingId = isEditing
                    ? parseInt(id!, 10)
                    : response.result?.id
                if (billingId && filesToUpload.length) {
                    await uploadPendingAttachments(billingId, filesToUpload)
                }

                // Parcelamento próprio: aplicado no submit final (já com o método
                // de pagamento de cada parcela). Vale no create e no edit — usa o
                // billingId recém-criado ou o existente.
                // Split de FORMAS de pagamento (parte cartão, parte dinheiro).
                // Na criação vai junto no payload; aqui trata a EDIÇÃO, que
                // redistribui o total entre as formas via endpoint próprio.
                if (splitItems.length > 0 && isEditing && billingId) {
                    const payload = buildSplitPayload()
                    if (!payload.ok) {
                        toast.error(payload.error)
                        return
                    }
                    const res = await updateBillingPaymentSplits(
                        billingId,
                        payload.lines,
                        showPaidAt ? effectivePaidAt : undefined
                    )
                    if (!res.success) {
                        toast.error(
                            res.error ||
                                'Não foi possível refazer as formas de pagamento'
                        )
                        return
                    }
                    toast.success('Formas de pagamento atualizadas')
                    // Com as formas já gravadas, o lote pode ir à fila — é este o
                    // caminho do "Salvar e enviar" de uma solicitação dividida.
                    if (await finishBatchSubmission()) return
                    if (onSuccess) onSuccess(billingId)
                    else navigate(returnPath(billingId))
                    return
                }

                if (installmentPlan && billingId) {
                    const res = await createInstallmentPlan(
                        billingId,
                        installmentPlan
                    )
                    if (!res.success) {
                        toast.error(
                            res.error || 'Não foi possível criar o parcelamento'
                        )
                        // A cobrança já foi gravada. Na criação, ficar nesta tela
                        // faria o próximo "Salvar" criar outra — segue para a edição
                        // dela, onde o parcelamento pode ser refeito.
                        if (!isEditing) {
                            navigate(`/financial/billings/${billingId}/edit`)
                        }
                        return
                    }
                    toast.success('Parcelamento criado com sucesso')
                    if (onSuccess) onSuccess(billingId)
                    else navigate(returnPath(billingId))
                    return
                }

                toast.success(
                    isEditing
                        ? `${label} atualizada com sucesso`
                        : `${label} criada com sucesso`
                )
                if (await finishBatchSubmission()) return
                if (onSuccess) onSuccess(billingId ?? undefined)
                else navigate(returnPath(billingId))
            } else {
                toast.error(
                    response.error ||
                        (isEditing
                            ? `Erro ao atualizar ${label.toLowerCase()}`
                            : `Erro ao criar ${label.toLowerCase()}`)
                )
            }
        } catch (error) {
            const label = isPayable ? 'conta a pagar' : 'cobrança'
            toast.error(
                isEditing
                    ? `Erro ao atualizar ${label}`
                    : `Erro ao criar ${label}`
            )
        } finally {
            // A gravação falhou: o pedido de envio morre aqui. Deixá-lo marcado faria
            // o próximo "Salvar" mandar o lote para a fila sem que ninguém pedisse.
            submitBatchAfterSaveRef.current = false
            setLoading(false)
        }
    }

    /**
     * Uma conta a pagar tem destinatário quando a FK do tipo escolhido no picker
     * está preenchida. Fornecedor também aceita nome livre, sem cadastro.
     */
    const hasPayableRecipient = (): boolean => {
        switch (formData.recipientType) {
            case 'SUPPLIER':
                return !!(formData.supplierId || formData.supplierName.trim())
            case 'CLIENT':
                return !!formData.clientId
            case 'PARTNER':
                return !!formData.partnerId
            case 'PROFESSIONAL':
                return !!formData.professionalId
            case 'HOSPITAL':
                return !!formData.hospitalId
            case 'USER':
                return !!formData.userId
            case 'EMPLOYEE':
                return !!formData.employeeId
            default:
                return false
        }
    }

    const getMissingFields = (): string[] => {
        const missing: string[] = []
        if (!formData.type) missing.push('Tipo')
        if (!formData.amount) missing.push('Valor')
        if (!formData.dueDate) missing.push('Vencimento')
        if (!formData.paymentMethod) missing.push('Forma de pagamento')
        if (isReceivable && !formData.clientId) missing.push('Cliente')
        // Pagador convênio sem convênio escolhido: o backend rejeita, e sem esta
        // checagem o operador só descobriria ao salvar.
        if (
            isReceivable &&
            formData.payerType === 'HEALTH_PLAN' &&
            !formData.healthPlanId
        )
            missing.push('Convênio pagador')
        if (isPayable && !hasPayableRecipient())
            missing.push(TYPE_LABELS[formData.recipientType])
        // Forma extra pela metade trava o salvamento: sem método o backend não sabe
        // como registrar a linha, e sem valor ela nasceria zerada dentro do split.
        if (splitItems.some(it => !it.paymentMethod))
            missing.push('Método das formas de pagamento adicionais')
        if (splitItems.some(it => parseCurrencyToInt(it.amount) <= 0))
            missing.push('Valor das formas de pagamento adicionais')
        return missing
    }

    const isFormValid = () => getMissingFields().length === 0

    // Modo IA: a forma de pagamento do grupo ou do pagamento dividido é escolhida aqui
    // (ver `billing-ai-bridge.ts`). Só na cobrança vinda do agendamento e ainda editável.
    useBillingAiBridge({
        enabled: isEditing && isFromSchedule && !loadingData && !!loadedBillingStatus && !isUneditableBilling,
        billingId: id ? Number(id) : 0,
        status: loadedBillingStatus,
        paymentMethod: formData.paymentMethod,
        installmentCount: formData.installmentCount ?? null,
        principalAmount: parseCurrencyToInt(formData.amount),
        parts: splitItems.map(item => ({
            method: item.paymentMethod,
            amount: parseCurrencyToInt(item.amount),
        })),
        availableMethods,
        busy: loading || loadingData,
        // Os mesmos campos do bloco principal: trocar a forma zera as parcelas, que só
        // valem no cartão.
        setPaymentMethod: (method, installments) =>
            setFormData(prev => ({ ...prev, paymentMethod: method, installmentCount: installments })),
        // Mesmo resultado de "Adicionar forma de pagamento" + valor em cada parte: o total
        // fica constante e a principal leva o resto.
        setParts: parts => {
            const total =
                parseCurrencyToInt(formData.amount) +
                splitItems.reduce((sum, item) => sum + parseCurrencyToInt(item.amount), 0)
            const partsTotal = parts.reduce((sum, part) => sum + part.amount, 0)
            setSplitItems(
                parts.map(part => ({
                    paymentMethod: part.method,
                    amount: formatCurrencyInput(String(part.amount)),
                    dueDate: formData.dueDate,
                }))
            )
            setFormData(prev => ({ ...prev, amount: formatCurrencyInput(String(total - partsTotal)) }))
        },
        save: () => handleSubmit({ preventDefault: () => {} } as React.FormEvent),
    })

    if (loadingData) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
                <div className="loading loading-spinner loading-lg text-secondary"></div>
                <p className="text-base-content/60 text-sm">Carregando...</p>
            </div>
        )
    }

    /**
     * Cobrança encerrada ou com dinheiro já movimentado: a tela não abre.
     *
     * Fica FORA do formulário (e não como campos desabilitados) porque não há nada a
     * salvar aqui — o backend recusa o PATCH inteiro. Mostrar o form preenchido só
     * convidaria o operador a digitar para descobrir no fim que não dava.
     */
    if (isUneditableBilling) {
        /**
         * Cobrança que nasce QUITADA valendo zero: atendimento coberto por convênio
         * (quem cobra é a fatura do período) e atendimento gratuito. O status é `PAID`,
         * mas dizer "já foi paga, estorne para corrigir" manda o operador para um
         * caminho que não existe — ninguém pagou nada, e não há o que estornar.
         */
        const isSettledZero =
            !isPayable &&
            loadedBillingStatus === 'PAID' &&
            parseCurrencyToInt(formData.amount) === 0
        const uneditableText = isSettledZero
            ? formData.payerType === 'HEALTH_PLAN'
                ? 'Este atendimento é coberto por convênio: a cobrança nasce quitada e valendo zero, porque quem cobra o convênio é a fatura do período. Não há nada a editar aqui — o valor do atendimento e o vencimento se acertam no convênio e na fatura.'
                : 'Esta cobrança nasceu quitada valendo zero (atendimento sem valor a receber). Não há nada a editar aqui.'
            : uneditableBillingReason(loadedBillingStatus)

        return (
            <div className="flex flex-col gap-5">
                <div className="rounded-2xl border border-base-300 bg-base-100 p-6 flex flex-col items-center gap-4 text-center">
                    <MdBlock className="text-4xl text-warning" />
                    <div className="flex flex-col gap-1">
                        <h2 className="text-lg font-semibold">
                            {isPayable
                                ? 'Conta a pagar não editável'
                                : 'Cobrança não editável'}
                        </h2>
                        <p className="text-sm text-base-content/70 max-w-xl">
                            {uneditableText}
                        </p>
                    </div>
                    <div className="flex gap-2">
                        <button
                            type="button"
                            className="btn btn-sm"
                            onClick={() =>
                                onBack
                                    ? onBack()
                                    : navigate('/financial/billings')
                            }
                        >
                            Voltar
                        </button>
                        {id && (
                            <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() =>
                                    navigate(`/financial/billings/${id}`)
                                }
                            >
                                Ver detalhes
                            </button>
                        )}
                    </div>
                </div>
            </div>
        )
    }

    const pageTitle = isFromScheduling
        ? 'Finalizar Pagamento'
        : isEditing
          ? isPayable
              ? 'Editar Conta a Pagar'
              : 'Editar Cobrança'
          : formData.type
            ? isPayable
                ? 'Nova Conta a Pagar'
                : 'Nova Cobrança'
            : 'Novo Lançamento'

    return (
        <div className="flex flex-col gap-5">
            {/* Pagar/receber vira a cor semântica do tema (error/success) em vez
                de um gradiente vermelho/verde cravado fora dele. */}
            <header className="relative overflow-hidden rounded-2xl border border-base-300 bg-base-100">
                <div
                    aria-hidden
                    className={`pointer-events-none absolute inset-0 bg-linear-to-r ${
                        isPayable
                            ? 'from-error/10 via-error/5 to-transparent'
                            : 'from-success/10 via-success/5 to-transparent'
                    }`}
                />
                <div className="relative flex items-center gap-4 p-5">
                    <button
                        type="button"
                        onClick={() =>
                            onBack ? onBack() : navigate('/financial/billings')
                        }
                        className="btn btn-ghost btn-circle btn-sm shrink-0"
                        title="Voltar"
                    >
                        <MdArrowBack className="text-xl" />
                    </button>
                    <div
                        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl ring-1 ${
                            isPayable
                                ? 'bg-error/10 text-error ring-error/20'
                                : 'bg-success/10 text-success ring-success/20'
                        }`}
                    >
                        <MdReceipt className="text-2xl" />
                    </div>
                    <div className="min-w-0 flex-1">
                        <p className="text-xs font-medium uppercase tracking-wide text-base-content/40">
                            {isEditing
                                ? 'Editando lançamento'
                                : 'Novo lançamento'}
                        </p>
                        <h1 className="truncate text-xl font-bold text-base-content sm:text-2xl">
                            {pageTitle}
                        </h1>
                    </div>
                    {formData.type && (
                        <span
                            className={`hidden shrink-0 items-center rounded-full border px-3 py-1.5 text-xs font-medium sm:inline-flex ${
                                isPayable
                                    ? 'border-error/30 bg-error/10 text-error'
                                    : 'border-success/30 bg-success/10 text-success'
                            }`}
                        >
                            {isPayable ? 'Conta a pagar' : 'Conta a receber'}
                        </span>
                    )}
                </div>
            </header>

            <form ref={formRef} onSubmit={handleSubmit} className="flex flex-col gap-5">
                {/* Seletor de Tipo - exibido apenas na criação e sem defaultType */}
                {!isEditing && !defaultType && (
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                        <button
                            type="button"
                            onClick={() => handleSelectType('RECEIVABLE')}
                            className={`flex flex-row items-center justify-center gap-3 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                                isReceivable
                                    ? 'border-success bg-success/10 shadow-md'
                                    : 'border-base-300 hover:border-success/50 hover:bg-success/5'
                            }`}
                        >
                            <div
                                className={`w-14 h-14 rounded-full flex items-center justify-center ${
                                    isReceivable
                                        ? 'bg-success text-success-content'
                                        : 'bg-success/10 text-success'
                                }`}
                            >
                                <MdTrendingUp className="text-3xl" />
                            </div>
                            <div className="text-left">
                                <p className="font-semibold text-base-content">
                                    Conta a Receber
                                </p>
                                <p className="text-xs text-base-content/60 mt-1">
                                    Cobranças de clientes/parceiros
                                </p>
                            </div>
                        </button>

                        <button
                            type="button"
                            onClick={() => handleSelectType('PAYABLE')}
                            className={`flex flex-row justify-center items-center gap-3 p-3 rounded-xl border-2 transition-all cursor-pointer ${
                                isPayable
                                    ? 'border-error bg-error/10 shadow-md'
                                    : 'border-base-300 hover:border-error/50 hover:bg-error/5'
                            }`}
                        >
                            <div
                                className={`w-14 h-14 rounded-full flex items-center justify-center ${
                                    isPayable
                                        ? 'bg-error text-error-content'
                                        : 'bg-error/10 text-error'
                                }`}
                            >
                                <MdTrendingDown className="text-3xl" />
                            </div>
                            <div className="text-left">
                                <p className="font-semibold text-base-content">
                                    Conta a Pagar
                                </p>
                                <p className="text-xs text-base-content/60 mt-1">
                                    Pagamentos a fornecedores e outros
                                </p>
                            </div>
                        </button>
                    </div>
                )}

                {/* Formulário - só aparece após escolher tipo (ou em edição) */}
                {formData.type && (
                    <>
                        {/* Dados do Lançamento */}
                        <FormSection
                            id="secao-dados"
                            icon={
                                isPayable ? (
                                    <MdStore className="text-lg" />
                                ) : (
                                    <MdPerson className="text-lg" />
                                )
                            }
                            title={
                                isPayable
                                    ? 'Dados da conta a pagar'
                                    : 'Dados da cobrança'
                            }
                            subtitle={
                                isPayable
                                    ? 'Para quem se paga e por quê'
                                    : 'De quem se cobra e por quê'
                            }
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                {/* Seletor de Cliente (apenas RECEIVABLE) */}
                                {isReceivable && (
                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text">
                                                Cliente{' '}
                                                <span className="text-error">
                                                    *
                                                </span>
                                            </span>
                                        </label>
                                        <ClientSelector
                                            value={formData.clientId ?? null}
                                            onChange={(clientId, client) => {
                                                if (client) {
                                                    handleSelectClient(client)
                                                } else {
                                                    handleClearClient()
                                                }
                                            }}
                                            label=""
                                            placeholder="Buscar cliente por nome, CPF, e-mail ou WhatsApp..."
                                            disabled={isFromScheduling}
                                        />
                                    </div>
                                )}

                                {/* Pagador: paciente ou convênio. Vem logo depois do
                                    cliente porque muda o significado do campo acima —
                                    com convênio, o paciente deixa de ser quem paga e
                                    passa a ser só o dono do atendimento. */}
                                {isReceivable && isHealth && (
                                    <div className="md:col-span-2">
                                        <div className="form-control w-full">
                                            <label className="label">
                                                <span className="label-text">
                                                    Quem paga
                                                </span>
                                            </label>
                                            <div className="flex flex-wrap gap-3">
                                                <label className="label cursor-pointer gap-2">
                                                    <input
                                                        type="radio"
                                                        name="payerType"
                                                        className="radio radio-secondary radio-sm"
                                                        disabled={isEditing}
                                                        checked={
                                                            formData.payerType ===
                                                            'CLIENT'
                                                        }
                                                        onChange={() =>
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                payerType:
                                                                    'CLIENT',
                                                                healthPlanId: null,
                                                            }))
                                                        }
                                                    />
                                                    <span className="label-text">
                                                        Paciente
                                                    </span>
                                                </label>
                                                <label className="label cursor-pointer gap-2">
                                                    <input
                                                        type="radio"
                                                        name="payerType"
                                                        className="radio radio-secondary radio-sm"
                                                        disabled={isEditing}
                                                        checked={
                                                            formData.payerType ===
                                                            'HEALTH_PLAN'
                                                        }
                                                        onChange={() =>
                                                            setFormData(prev => ({
                                                                ...prev,
                                                                payerType:
                                                                    'HEALTH_PLAN',
                                                            }))
                                                        }
                                                    />
                                                    <span className="label-text">
                                                        Convênio
                                                    </span>
                                                </label>
                                            </div>
                                            {isEditing && (
                                                <span className="text-xs text-base-content/50 mt-1">
                                                    O pagador não muda depois que
                                                    a cobrança existe. Se estiver
                                                    errado, cancele e gere outra.
                                                </span>
                                            )}
                                        </div>

                                        {formData.payerType ===
                                            'HEALTH_PLAN' && (
                                            <div className="mt-4">
                                                <HealthPlanSelector
                                                    value={formData.healthPlanId}
                                                    onChange={healthPlanId =>
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            healthPlanId,
                                                        }))
                                                    }
                                                    clientId={formData.clientId}
                                                    disabled={isEditing}
                                                    label="Convênio pagador *"
                                                    placeholder="Buscar convênio..."
                                                />
                                                <p className="text-xs text-base-content/60 mt-2">
                                                    A cobrança é faturada ao
                                                    convênio: sem link de
                                                    pagamento, sem PIX/boleto e
                                                    fora da régua de cobrança do
                                                    paciente. O vencimento segue o
                                                    prazo configurado no convênio.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Destinatário (apenas PAYABLE) — picker unificado */}
                                {isPayable && (
                                    <div className="md:col-span-2">
                                        <PayableRecipientPicker
                                            value={{
                                                type: formData.recipientType,
                                                supplierId: formData.supplierId,
                                                supplierName:
                                                    formData.supplierName,
                                                clientId: formData.clientId,
                                                partnerId: formData.partnerId,
                                                professionalId:
                                                    formData.professionalId,
                                                hospitalId: formData.hospitalId,
                                                userId: formData.userId,
                                                employeeId: formData.employeeId,
                                            }}
                                            onChange={v =>
                                                setFormData(prev => ({
                                                    ...prev,
                                                    // Repasse escolhido para outro tipo
                                                    // de destinatário não é agrupável em
                                                    // nome de ninguém: cai fora junto com
                                                    // o destinatário que o justificava.
                                                    category:
                                                        isPayoutCategory(
                                                            prev.category
                                                        ) &&
                                                        !manualPayableCategoryOptions(
                                                            v.type
                                                        ).some(
                                                            o =>
                                                                o.value ===
                                                                prev.category
                                                        )
                                                            ? ''
                                                            : prev.category,
                                                    recipientType: v.type,
                                                    supplierId:
                                                        v.supplierId ?? null,
                                                    supplierName:
                                                        v.supplierName ?? '',
                                                    clientId:
                                                        v.clientId ?? null,
                                                    partnerId:
                                                        v.partnerId ?? null,
                                                    professionalId:
                                                        v.professionalId ??
                                                        null,
                                                    hospitalId:
                                                        v.hospitalId ?? null,
                                                    userId: v.userId ?? null,
                                                    employeeId:
                                                        v.employeeId ?? null,
                                                }))
                                            }
                                        />
                                        {/* Categoria gravada pelo próprio sistema
                                            (repasse automático, repasse agrupado):
                                            rótulo em português e bloqueada. É por este
                                            código que o agrupamento encontra o
                                            lançamento — trocado à mão, ele sumiria do
                                            modal de geração sem erro nenhum. */}
                                        {isSystemGeneratedBilling ? (
                                            <div className="form-control w-full mt-3">
                                                <label className="label">
                                                    <span className="label-text">
                                                        Origem do lançamento
                                                    </span>
                                                </label>
                                                <div className="input input-bordered flex items-center bg-base-200 opacity-70 w-full">
                                                    {billingCategoryLabel(
                                                        formData.category,
                                                        serviceLabels
                                                    )}
                                                </div>
                                                <span className="text-xs text-base-content/50 mt-1">
                                                    Lançado pelo sistema. Para
                                                    classificar no plano de contas,
                                                    use a Categoria Financeira.
                                                </span>
                                            </div>
                                        ) : (
                                            <div className="form-control w-full mt-3">
                                                <label className="label">
                                                    <span className="label-text">
                                                        Categoria
                                                    </span>
                                                </label>
                                                <select
                                                    className="select select-bordered w-full"
                                                    value={
                                                        isCustomCategory
                                                            ? CUSTOM_CATEGORY_OPTION
                                                            : formData.category
                                                    }
                                                    onChange={e =>
                                                        handleCategoryOptionChange(
                                                            e.target.value
                                                        )
                                                    }
                                                >
                                                    <option value="">
                                                        Sem categoria
                                                    </option>
                                                    {categoryOptions.map(
                                                        option => (
                                                            <option
                                                                key={option.value}
                                                                value={
                                                                    option.value
                                                                }
                                                            >
                                                                {option.label}
                                                            </option>
                                                        )
                                                    )}
                                                    <option
                                                        value={
                                                            CUSTOM_CATEGORY_OPTION
                                                        }
                                                    >
                                                        Personalizado
                                                    </option>
                                                </select>
                                                {isCustomCategory && (
                                                    <input
                                                        type="text"
                                                        name="category"
                                                        placeholder="Ex: Aluguel, Material, Serviço..."
                                                        className="input input-bordered w-full mt-2"
                                                        value={formData.category}
                                                        onChange={handleChange}
                                                    />
                                                )}
                                                <span className="text-xs text-base-content/50 mt-1">
                                                    {selectedCategoryHint ??
                                                        'Categoria personalizada não entra no agrupamento de repasses. Para pagar um parceiro, profissional, hospital ou fornecedor junto com os demais, escolha o repasse correspondente.'}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Parceiro e Profissional: apenas RECEIVABLE (PAYABLE usa o picker).
                                    Vindo de agendamento/orçamento: só exibe o que existe e bloqueia. */}
                                {isReceivable &&
                                    (isFromScheduling ? (
                                        formData.partnerId ? (
                                            <div className="form-control w-full">
                                                <label className="label">
                                                    <span className="label-text">
                                                        Parceiro
                                                    </span>
                                                </label>
                                                <div className="input input-bordered flex items-center bg-base-200 opacity-70 w-full">
                                                    {schedulePartnerName ||
                                                        `Parceiro #${formData.partnerId}`}
                                                </div>
                                            </div>
                                        ) : null
                                    ) : (
                                        canSeePartners && <PartnerSelector
                                            value={formData.partnerId}
                                            onChange={partnerId =>
                                                setFormData(prev => ({
                                                    ...prev,
                                                    partnerId,
                                                }))
                                            }
                                        />
                                    ))}

                                {isReceivable &&
                                    (isFromScheduling ? (
                                        formData.professionalId ? (
                                            <div className="form-control w-full">
                                                <label className="label">
                                                    <span className="label-text">
                                                        Profissional
                                                    </span>
                                                </label>
                                                <div className="input input-bordered flex items-center bg-base-200 opacity-70">
                                                    {scheduleProfessionalName ||
                                                        `Profissional #${formData.professionalId}`}
                                                </div>
                                            </div>
                                        ) : null
                                    ) : (
                                        canSeeProfessionals && <ProfessionalSearchSelector
                                            value={formData.professionalId}
                                            onChange={professionalId =>
                                                setFormData(prev => ({
                                                    ...prev,
                                                    professionalId,
                                                }))
                                            }
                                        />
                                    ))}

                                {/* Hospital herdado do atendimento: só quando existe, bloqueado
                                    (HospitalSelector disabled) para não mudar aqui. */}
                                {isReceivable &&
                                    isFromScheduling &&
                                    formData.hospitalId && (
                                        <HospitalSearchSelector
                                            label="Hospital"
                                            value={formData.hospitalId}
                                            onChange={() => {}}
                                            disabled
                                        />
                                    )}

                                {/* Composição herdada do atendimento (materiais + itens
                                    de catálogo), snapshot congelado na geração da cobrança. */}
                                <BillingComposition
                                    catalogItems={
                                        billingComposition.catalogItems
                                    }
                                    medicalSupplies={
                                        billingComposition.medicalSupplies
                                    }
                                />

                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text">
                                            Descrição
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        name="description"
                                        placeholder={
                                            isPayable
                                                ? 'Descrição da conta'
                                                : 'Descrição da cobrança'
                                        }
                                        className="input input-bordered w-full"
                                        value={formData.description}
                                        onChange={handleChange}
                                    />
                                </div>

                                {/*<div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text">
                                            Código de Referência
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        name="referenceCode"
                                        placeholder="Código interno"
                                        className="input input-bordered w-full"
                                        value={formData.referenceCode}
                                        onChange={handleChange}
                                    />
                                </div>*/}

                                <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text">
                                            Categoria Financeira
                                        </span>
                                    </label>
                                    <select
                                        className="select select-bordered w-full"
                                        value={
                                            formData.financialCategoryId || ''
                                        }
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                financialCategoryId: e.target
                                                    .value
                                                    ? parseInt(
                                                          e.target.value,
                                                          10
                                                      )
                                                    : null,
                                            }))
                                        }
                                    >
                                        <option value="">
                                            Selecione a categoria
                                        </option>
                                        {financialCategories.map(fc => (
                                            <option key={fc.id} value={fc.id}>
                                                {fc.code ? `${fc.code} - ` : ''}
                                                {fc.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text">
                                            Centro de Custo
                                        </span>
                                    </label>
                                    <select
                                        className="select select-bordered w-full"
                                        value={formData.costCenterId || ''}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                costCenterId: e.target.value
                                                    ? parseInt(
                                                          e.target.value,
                                                          10
                                                      )
                                                    : null,
                                            }))
                                        }
                                    >
                                        <option value="">
                                            Selecione o centro de custo
                                        </option>
                                        {costCenters.map(cc => (
                                            <option key={cc.id} value={cc.id}>
                                                {cc.code} - {cc.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>*/}

                                {/* <div className="form-control w-full">
                                    <label className="label">
                                        <span className="label-text">
                                            Conta Bancária
                                        </span>
                                    </label>
                                    <select
                                        className="select select-bordered w-full"
                                        value={formData.bankAccountId || ''}
                                        onChange={e =>
                                            setFormData(prev => ({
                                                ...prev,
                                                bankAccountId: e.target.value
                                                    ? parseInt(
                                                          e.target.value,
                                                          10
                                                      )
                                                    : null,
                                            }))
                                        }
                                    >
                                        <option value="">
                                            Selecione a conta
                                        </option>
                                        {bankAccounts.map(ba => (
                                            <option key={ba.id} value={ba.id}>
                                                {ba.name} - {ba.bankName}
                                            </option>
                                        ))}
                                    </select>
                                </div>*/}
                            </div>
                        </FormSection>

                        {/* Valores e Vencimento */}
                        <FormSection
                            id="secao-valores"
                            icon={<MdAttachMoney className="text-lg" />}
                            title="Valores e vencimento"
                            subtitle="Quanto, como e quando"
                        >
                            <div className="bloco-principal mb-3 border border-base-300 rounded p-3 py-3">
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text">
                                                Valor (R$){' '}
                                                {!isAmountReadOnly && (
                                                    <span className="text-error">
                                                        *
                                                    </span>
                                                )}
                                            </span>
                                        </label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/50 font-medium z-10 pointer-events-none">
                                                R$
                                            </span>
                                            {/* Solicitação agrupada: o valor nasce da
                                                SOMA dos repasses, não de um número
                                                digitado — por isso o campo é fechado
                                                por padrão. Quem tem permissão de editar
                                                cobranças pode corrigi-lo mesmo assim
                                                (glosa do parceiro, acerto combinado
                                                fora do sistema), assumindo que a conta
                                                passa a divergir dos lançamentos que ela
                                                representa. */}
                                            <input
                                                type="text"
                                                placeholder="0,00"
                                                className={`input input-bordered w-full pl-10 ${
                                                    isAmountReadOnly
                                                        ? 'bg-base-200 opacity-70'
                                                        : ''
                                                }`}
                                                value={formData.amount}
                                                readOnly={isAmountReadOnly}
                                                onChange={handleCurrencyChange(
                                                    'amount'
                                                )}
                                            />
                                        </div>
                                        {existingFeeAmount > 0 && (
                                            <span className="text-xs text-base-content/50 mt-1">
                                                Sem a taxa do método. A taxa de R${' '}
                                                {intToCurrencyDisplay(
                                                    existingFeeAmount
                                                )}{' '}
                                                é somada à cobrança ao salvar.
                                            </span>
                                        )}
                                        {isPayoutBatchBilling && (
                                            <span
                                                className={`text-xs mt-1 ${
                                                    isAmountReadOnly
                                                        ? 'text-base-content/50'
                                                        : 'text-warning'
                                                }`}
                                            >
                                                {isAmountReadOnly
                                                    ? 'Soma dos repasses agrupados. Para mudar, retire um lançamento da solicitação.'
                                                    : 'Soma dos repasses agrupados. Alterar o valor aqui descola a conta dos lançamentos que ela representa — retirar um lançamento continua sendo o caminho normal.'}
                                            </span>
                                        )}
                                        {isAppointmentReceivable && (
                                            <span className="text-xs text-base-content/50 mt-1">
                                                Valor definido n
                                                {originArticles[
                                                    billingOrigin!.type
                                                ] ?? 'o'}{' '}
                                                {(
                                                    originLabels[
                                                        billingOrigin!.type
                                                    ] ?? 'atendimento'
                                                ).toLowerCase()}
                                                . Para cobrar menos, use o
                                                desconto; para cobrar mais, gere
                                                uma cobrança complementar.
                                            </span>
                                        )}
                                    </div>

                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text">
                                                Desconto (R$)
                                                {maxDiscountPercent != null && (
                                                    <span className="text-xs text-warning ml-1">
                                                        (max{' '}
                                                        {maxDiscountPercent}%)
                                                    </span>
                                                )}
                                            </span>
                                        </label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/50 font-medium z-10 pointer-events-none">
                                                R$
                                            </span>
                                            <input
                                                type="text"
                                                placeholder="0,00"
                                                className="input input-bordered w-full pl-10"
                                                value={formData.discount}
                                                onChange={e => {
                                                    handleCurrencyChange(
                                                        'discount'
                                                    )(e)
                                                    // Validar limite de desconto após atualizar
                                                    if (
                                                        maxDiscountPercent !=
                                                            null &&
                                                        Number(
                                                            formData.amount
                                                        ) > 0
                                                    ) {
                                                        const maxValue =
                                                            Math.round(
                                                                (Number(
                                                                    formData.amount
                                                                ) *
                                                                    maxDiscountPercent) /
                                                                    100
                                                            )
                                                        const currentDiscount =
                                                            parseInt(
                                                                e.target.value.replace(
                                                                    /\D/g,
                                                                    ''
                                                                )
                                                            ) || 0
                                                        if (
                                                            currentDiscount >
                                                            maxValue
                                                        ) {
                                                            setTimeout(() => {
                                                                setFormData(
                                                                    prev => ({
                                                                        ...prev,
                                                                        discount:
                                                                            String(
                                                                                maxValue
                                                                            ),
                                                                    })
                                                                )
                                                                toast.warning(
                                                                    `Desconto limitado a ${maxDiscountPercent}% do valor`
                                                                )
                                                            }, 100)
                                                        }
                                                    }
                                                }}
                                            />
                                        </div>
                                    </div>

                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text">
                                                Juros/Multa (R$)
                                            </span>
                                        </label>
                                        <div className="relative">
                                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/50 font-medium z-10 pointer-events-none">
                                                R$
                                            </span>
                                            <input
                                                type="text"
                                                placeholder="0,00"
                                                className="input input-bordered w-full pl-10"
                                                value={formData.interest}
                                                onChange={handleCurrencyChange(
                                                    'interest'
                                                )}
                                            />
                                        </div>
                                    </div>

                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text">
                                                Data de Vencimento{' '}
                                                <span className="text-error">
                                                    *
                                                </span>
                                            </span>
                                        </label>
                                        <input
                                            type="date"
                                            name="dueDate"
                                            className="input input-bordered w-full"
                                            value={formData.dueDate}
                                            onChange={handleChange}
                                            required
                                        />
                                    </div>

                                    {showPaidAt && (
                                        <div className="form-control w-full">
                                            <label className="label">
                                                <span className="label-text">
                                                    Pago em
                                                </span>
                                            </label>
                                            <input
                                                type="date"
                                                name="paidAt"
                                                className="input input-bordered w-full"
                                                value={effectivePaidAt}
                                                max={localTodayInput()}
                                                onChange={handleChange}
                                            />
                                            <label className="label">
                                                <span className="label-text-alt text-base-content/60">
                                                    Dia em que o valor recebido por fora
                                                    entrou. A cobrança é baixada ao salvar.
                                                </span>
                                            </label>
                                        </div>
                                    )}

                                    <div className="form-control w-full">
                                        <label className="label">
                                            <span className="label-text">
                                                Método de Pagamento{' '}
                                                <span className="text-error">
                                                    *
                                                </span>
                                            </span>
                                        </label>
                                        <select
                                            name="paymentMethod"
                                            className="select select-bordered w-full"
                                            value={formData.paymentMethod}
                                            onChange={handleChange}
                                        >
                                            <option value="">
                                                Selecione o método *
                                            </option>
                                            {PAYMENT_METHODS.filter(
                                                m =>
                                                    availableMethods.includes(
                                                        m.value
                                                    ) &&
                                                    // Método já ocupado por uma forma extra.
                                                    !usedPaymentMethods().includes(
                                                        m.value
                                                    )
                                            ).map(m => (
                                                <option
                                                    key={m.value}
                                                    value={m.value}
                                                >
                                                    {m.label}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                {/* Parcelamento combinado com o pagador.
                                    MESMO componente do agendamento, de propósito: a
                                    conta da taxa por faixa é uma só, e duas telas
                                    calculando o mesmo número já divergiram antes.
                                    É aqui que o paciente que desistiu de 3x e quer 4x
                                    é resolvido. */}
                                {isReceivable &&
                                    formData.paymentMethod ===
                                        'CREDIT_CARD' && (
                                        <PaymentOptions
                                            amount={
                                                parseCurrencyToInt(
                                                    formData.amount
                                                )
                                            }
                                            methods={['CREDIT_CARD']}
                                            context="billing"
                                            selectedMethod="CREDIT_CARD"
                                            selectedInstallments={
                                                formData.installmentCount
                                            }
                                            onSelect={option =>
                                                setFormData(prev => ({
                                                    ...prev,
                                                    installmentCount:
                                                        option.installments > 1
                                                            ? option.installments
                                                            : null,
                                                }))
                                            }
                                        />
                                    )}

                                {(formData.paymentMethod === 'CREDIT_CARD' ||
                                    formData.paymentMethod === 'DEBIT_CARD') &&
                                    terminals.length > 0 && (
                                        <div className="form-control w-full">
                                            <label className="label">
                                                <span className="label-text">
                                                    Terminal de Pagamento
                                                </span>
                                            </label>
                                            <select
                                                className="select select-bordered w-full"
                                                value={selectedTerminalId || ''}
                                                onChange={e => {
                                                    const val = e.target.value
                                                        ? parseInt(
                                                              e.target.value,
                                                              10
                                                          )
                                                        : null
                                                    setSelectedTerminalId(val)
                                                    if (val)
                                                        localStorage.setItem(
                                                            'lastUsedTerminalId',
                                                            String(val)
                                                        )
                                                    else
                                                        localStorage.removeItem(
                                                            'lastUsedTerminalId'
                                                        )
                                                }}
                                            >
                                                <option value="">
                                                    Sem terminal (pagamento
                                                    online)
                                                </option>
                                                {terminals.map(t => (
                                                    <option
                                                        key={t.id}
                                                        value={t.id}
                                                    >
                                                        {t.name} (
                                                        {t.serialNumber}) -{' '}
                                                        {t.provider ||
                                                            'Provider'}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    )}

                                {/* Cálculo de troco - apenas para dinheiro */}
                                {formData.paymentMethod === 'CASH' &&
                                    Number(formData.amount) > 0 && (
                                        <div className="form-control w-full">
                                            <label className="label">
                                                <span className="label-text">
                                                    Valor recebido
                                                </span>
                                            </label>
                                            <input
                                                type="number"
                                                className="input input-bordered w-full"
                                                placeholder="0,00"
                                                min={0}
                                                step="0.01"
                                                onChange={e => {
                                                    const received =
                                                        parseFloat(
                                                            e.target.value
                                                        ) || 0
                                                    const receivedCents =
                                                        Math.round(
                                                            received * 100
                                                        )
                                                    const amountCents = Number(
                                                        formData.amount
                                                    )
                                                    const changeCents =
                                                        receivedCents -
                                                        amountCents
                                                    const changeEl =
                                                        document.getElementById(
                                                            'change-display'
                                                        )
                                                    if (changeEl) {
                                                        if (
                                                            receivedCents > 0 &&
                                                            changeCents >= 0
                                                        ) {
                                                            changeEl.textContent = `Troco: R$ ${(changeCents / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                                            changeEl.className =
                                                                'text-sm font-bold text-success mt-1'
                                                        } else if (
                                                            receivedCents > 0 &&
                                                            changeCents < 0
                                                        ) {
                                                            changeEl.textContent = `Faltam: R$ ${(Math.abs(changeCents) / 100).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                                            changeEl.className =
                                                                'text-sm font-bold text-error mt-1'
                                                        } else {
                                                            changeEl.textContent =
                                                                ''
                                                        }
                                                    }
                                                }}
                                            />
                                            <div
                                                id="change-display"
                                                className="mt-1"
                                            ></div>
                                            <p className="text-xs opacity-50 mt-1">
                                                Total: R${' '}
                                                {(Number(formData.amount) / 100)
                                                    .toLocaleString('pt-BR', {
                                                        minimumFractionDigits: 2,
                                                        maximumFractionDigits: 2,
                                                    })
                                                    .replace('.', ',')}
                                            </p>
                                        </div>
                                    )}
                            </div>

                            {/* FORMAS DE PAGAMENTO ADICIONAIS — cópias do bloco acima, cada uma com
                                método, valor e vencimento próprios. O que se lança aqui SAI do bloco
                                principal, então o total nunca cresce sozinho. */}
                            {!isClosedBilling &&
                                splitItems.map((item, index) => (
                                    <div
                                        className="bloco-principal mb-3 border border-base-300 rounded p-3 py-3"
                                        key={index}
                                    >
                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                            <div className="form-control w-full">
                                                <label className="label">
                                                    <span className="label-text">
                                                        Valor *
                                                    </span>
                                                </label>
                                                <div className="relative">
                                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-base-content/60 z-10">
                                                        R$
                                                    </span>
                                                    <input
                                                        type="text"
                                                        placeholder="0,00"
                                                        className="input input-bordered w-full pl-10"
                                                        value={item.amount}
                                                        onChange={e =>
                                                            setSplitItemField(
                                                                index,
                                                                'amount',
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </div>
                                            </div>

                                            <div className="form-control w-full">
                                                <label className="label">
                                                    <span className="label-text">
                                                        Data de Vencimento
                                                    </span>
                                                </label>
                                                <input
                                                    type="date"
                                                    className="input input-bordered w-full"
                                                    value={item.dueDate}
                                                    onChange={e =>
                                                        setSplitItemField(
                                                            index,
                                                            'dueDate',
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </div>

                                            <div className="form-control w-full">
                                                <label className="label">
                                                    <span className="label-text">
                                                        Método de Pagamento *
                                                    </span>
                                                </label>
                                                <select
                                                    className="select select-bordered w-full"
                                                    value={item.paymentMethod}
                                                    onChange={e =>
                                                        setSplitItemField(
                                                            index,
                                                            'paymentMethod',
                                                            e.target.value
                                                        )
                                                    }
                                                >
                                                    <option value="">
                                                        Selecione...
                                                    </option>
                                                    {PAYMENT_METHODS.filter(
                                                        m =>
                                                            availableMethods.includes(
                                                                m.value
                                                            ) &&
                                                            // Ocupado pelo bloco principal
                                                            // ou por outra forma extra.
                                                            !usedPaymentMethods(
                                                                index
                                                            ).includes(m.value)
                                                    ).map(m => (
                                                        <option
                                                            key={m.value}
                                                            value={m.value}
                                                        >
                                                            {m.label}
                                                        </option>
                                                    ))}
                                                </select>
                                            </div>
                                        </div>
                                        <div className="flex items-center justify-between gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-ghost btn-sm text-error"
                                                onClick={() =>
                                                    removeSplitItem(index)
                                                }
                                            >
                                                <MdDelete /> Remover
                                            </button>
                                        </div>
                                    </div>
                                ))}

                            {!isClosedBilling && (
                                <button
                                    type="button"
                                    className="btn btn-outline btn-secondary gap-2 self-start mt-3"
                                    onClick={addSplitItem}
                                    disabled={
                                        parseCurrencyToInt(formData.amount) <=
                                            0 || !hasSparePaymentMethod
                                    }
                                    title={
                                        parseCurrencyToInt(formData.amount) <= 0
                                            ? 'Informe o valor da cobrança primeiro'
                                            : !hasSparePaymentMethod
                                              ? 'Todos os métodos de pagamento disponíveis já estão em uso'
                                              : 'O valor lançado aqui sai do bloco acima'
                                    }
                                >
                                    <MdPayment className="text-base" />
                                    Adicionar forma de pagamento
                                </button>
                            )}
                        </FormSection>

                        {/* Parcelamento no terminal */}
                        {selectedTerminalId &&
                            (formData.paymentMethod === 'CREDIT_CARD' ||
                                formData.paymentMethod === 'DEBIT_CARD') && (
                                <FormSection
                                    id="secao-terminal"
                                    icon={<MdCreditCard className="text-lg" />}
                                    title="Parcelamento no terminal"
                                    subtitle="Divisão feita na maquininha"
                                >
                                    <div className="form-control w-full max-w-xs">
                                        <label className="label">
                                            <span className="label-text">
                                                Número de parcelas
                                            </span>
                                        </label>
                                        <select
                                            className="select select-bordered w-full"
                                            value={
                                                formData.terminalInstallments
                                            }
                                            onChange={e =>
                                                setFormData(prev => ({
                                                    ...prev,
                                                    terminalInstallments:
                                                        e.target.value,
                                                }))
                                            }
                                        >
                                            {Array.from(
                                                { length: 12 },
                                                (_, i) => i + 1
                                            ).map(n => (
                                                <option key={n} value={n}>
                                                    {n}x{' '}
                                                    {Number(formData.amount) > 0
                                                        ? `de R$ ${(Number(formData.amount) / 100 / n).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                                        : ''}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </FormSection>
                            )}

                        {/* Parcelamento próprio (entrada + parcelas) — cobrança a
                            receber do próprio cliente, no create e no edit */}
                        {installAllowed && (
                            <FormSection
                                id="secao-parcelamento"
                                icon={<MdReceipt className="text-lg" />}
                                title="Parcelamento próprio da empresa"
                                subtitle="Divide em cobranças separadas, uma por parcela — não é o parcelamento do cartão"
                                actions={
                                    <input
                                        type="checkbox"
                                        aria-label="Ativar parcelamento"
                                        className="toggle toggle-secondary toggle-sm"
                                        checked={installOpen}
                                        onChange={e =>
                                            setInstallOpen(e.target.checked)
                                        }
                                    />
                                }
                            >
                                {installOpen && (
                                    <div className="flex flex-col gap-4">
                                        {/* O alerta que existia aqui repetia o que o
                                            título e o subtítulo já dizem. A confusão
                                            era com o NOME da seção ("Parcelamento",
                                            que todos liam como parcelas do cartão) —
                                            um aviso dentro dela só era visto por quem
                                            já tinha entrado no lugar errado. */}
                                        <p className="text-xs text-base-content/60">
                                            Divide esta cobrança em entrada +
                                            parcelas, cada uma com vencimento
                                            próprio. A cobrança atual vira a
                                            entrada (ou a 1ª parcela) e as demais
                                            são geradas na hora. Para parcelar no
                                            cartão, use a forma de pagamento.
                                        </p>

                                        <label className="label cursor-pointer justify-start gap-3">
                                            <input
                                                type="checkbox"
                                                className="checkbox checkbox-secondary"
                                                checked={installHasEntry}
                                                onChange={e =>
                                                    setInstallHasEntry(
                                                        e.target.checked
                                                    )
                                                }
                                            />
                                            <span className="label-text">
                                                Tem entrada
                                            </span>
                                        </label>

                                        {installHasEntry && (
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                <div className="form-control">
                                                    <label className="label">
                                                        <span className="label-text font-medium">
                                                            Valor da entrada
                                                        </span>
                                                    </label>
                                                    <input
                                                        type="text"
                                                        className="input input-bordered"
                                                        placeholder="R$ 0,00"
                                                        value={
                                                            installEntryAmount
                                                        }
                                                        onChange={e =>
                                                            setInstallEntryAmount(
                                                                formatCurrencyInput(
                                                                    e.target
                                                                        .value
                                                                )
                                                            )
                                                        }
                                                    />
                                                </div>
                                                <div className="form-control">
                                                    <label className="label">
                                                        <span className="label-text font-medium">
                                                            Vencimento da
                                                            entrada
                                                        </span>
                                                    </label>
                                                    <input
                                                        type="date"
                                                        className="input input-bordered"
                                                        value={installEntryDate}
                                                        onChange={e =>
                                                            setInstallEntryDate(
                                                                e.target.value
                                                            )
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        )}

                                        <div className="flex items-end gap-3">
                                            <div className="form-control">
                                                <label className="label">
                                                    <span className="label-text font-medium">
                                                        Nº de parcelas
                                                    </span>
                                                </label>
                                                <input
                                                    type="number"
                                                    min="1"
                                                    className="input input-bordered w-28"
                                                    value={installCount}
                                                    onChange={e =>
                                                        setInstallCount(
                                                            e.target.value
                                                        )
                                                    }
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                className="btn btn-ghost"
                                                onClick={generateInstallments}
                                            >
                                                Gerar parcelas
                                            </button>
                                        </div>

                                        {(() => {
                                            const count = Math.max(
                                                0,
                                                parseInt(installCount, 10) || 0
                                            )
                                            const total = parseCurrencyToInt(
                                                formData.amount
                                            )
                                            const entry = installHasEntry
                                                ? parseCurrencyToInt(
                                                      installEntryAmount
                                                  )
                                                : 0
                                            const remaining = Math.max(
                                                0,
                                                total - entry
                                            )
                                            if (count < 1 || remaining <= 0)
                                                return null
                                            const perParcela = Math.floor(
                                                remaining / count
                                            )
                                            return (
                                                <p className="text-xs text-base-content/60">
                                                    {count}× de aprox.{' '}
                                                    <span className="font-medium text-base-content">
                                                        {intToCurrencyDisplay(
                                                            perParcela
                                                        )}
                                                    </span>
                                                    {installHasEntry
                                                        ? ' (após descontar a entrada)'
                                                        : ''}{' '}
                                                    — o arredondamento vai na 1ª
                                                    parcela ao gerar.
                                                </p>
                                            )
                                        })()}

                                        {installItems.length > 0 && (
                                            <div className="flex flex-col gap-2">
                                                {installHasEntry && (
                                                    <div className="grid grid-cols-[auto_1fr] gap-2 items-center text-sm">
                                                        <span className="text-xs font-medium text-secondary w-20">
                                                            Entrada
                                                        </span>
                                                        <span className="text-base-content/70">
                                                            {installEntryAmount ||
                                                                'R$ 0,00'}{' '}
                                                            ·{' '}
                                                            {installEntryDate ||
                                                                formData.dueDate ||
                                                                '—'}
                                                        </span>
                                                    </div>
                                                )}
                                                {installItems.map((it, i) => (
                                                    <div
                                                        key={i}
                                                        className="grid grid-cols-[auto_1fr_1fr_1.2fr] gap-2 items-center"
                                                    >
                                                        <span className="text-xs text-base-content/60 w-20">
                                                            Parcela {i + 1}
                                                        </span>
                                                        <input
                                                            type="text"
                                                            className="input input-bordered input-sm"
                                                            value={it.amount}
                                                            onChange={e =>
                                                                handleInstallAmountChange(
                                                                    i,
                                                                    e.target
                                                                        .value
                                                                )
                                                            }
                                                        />
                                                        <input
                                                            type="date"
                                                            className="input input-bordered input-sm"
                                                            value={it.dueDate}
                                                            onChange={e =>
                                                                setInstallItems(
                                                                    prev =>
                                                                        prev.map(
                                                                            (
                                                                                p,
                                                                                idx
                                                                            ) =>
                                                                                idx ===
                                                                                i
                                                                                    ? {
                                                                                          ...p,
                                                                                          dueDate:
                                                                                              e
                                                                                                  .target
                                                                                                  .value,
                                                                                      }
                                                                                    : p
                                                                        )
                                                                )
                                                            }
                                                        />
                                                        <select
                                                            className="select select-bordered select-sm"
                                                            value={
                                                                it.paymentMethod
                                                            }
                                                            onChange={e =>
                                                                setInstallItemMethod(
                                                                    i,
                                                                    e.target
                                                                        .value
                                                                )
                                                            }
                                                        >
                                                            <option value="">
                                                                Método...
                                                            </option>
                                                            {PAYMENT_METHODS.filter(
                                                                m =>
                                                                    availableMethods.includes(
                                                                        m.value
                                                                    )
                                                            ).map(m => (
                                                                <option
                                                                    key={
                                                                        m.value
                                                                    }
                                                                    value={
                                                                        m.value
                                                                    }
                                                                >
                                                                    {m.label}
                                                                </option>
                                                            ))}
                                                        </select>
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </FormSection>
                        )}

                        {/* Recorrência */}
                        {
                            <FormSection
                                id="secao-recorrencia"
                                icon={<MdRepeat className="text-lg" />}
                                title="Recorrência"
                                subtitle="Repetição automática desta cobrança"
                            >
                                <div className="form-control">
                                    <label className="label cursor-pointer flex flex-row! items-center gap-3!">
                                        <input
                                            type="checkbox"
                                            className="checkbox checkbox-secondary"
                                            checked={formData.enableRecurring}
                                            onChange={e =>
                                                setFormData(prev => ({
                                                    ...prev,
                                                    enableRecurring:
                                                        e.target.checked,
                                                }))
                                            }
                                        />
                                        <span className="label-text font-medium">
                                            {isPayable
                                                ? 'Ativar conta a pagar recorrente'
                                                : 'Ativar cobrança recorrente'}
                                        </span>
                                    </label>
                                </div>

                                {formData.enableRecurring && (
                                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-4">
                                        <div className="form-control w-full">
                                            <label className="label">
                                                <span className="label-text">
                                                    Dia do vencimento
                                                </span>
                                            </label>
                                            <select
                                                className="select select-bordered w-full"
                                                value={formData.recurringDueDay}
                                                onChange={e =>
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        recurringDueDay: Number(
                                                            e.target.value
                                                        ),
                                                    }))
                                                }
                                            >
                                                {Array.from(
                                                    { length: 28 },
                                                    (_, i) => i + 1
                                                ).map(day => (
                                                    <option
                                                        key={day}
                                                        value={day}
                                                    >
                                                        Dia {day}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                        <div className="form-control w-full">
                                            <label className="label">
                                                <span className="label-text">
                                                    Período
                                                </span>
                                            </label>
                                            <select
                                                className="select select-bordered w-full"
                                                value={
                                                    formData.recurringInterval
                                                }
                                                onChange={e =>
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        recurringInterval:
                                                            e.target.value,
                                                    }))
                                                }
                                            >
                                                <option value="MONTHLY">
                                                    Mensal
                                                </option>
                                                <option value="QUARTERLY">
                                                    Trimestral
                                                </option>
                                                <option value="SEMIANNUAL">
                                                    Semestral
                                                </option>
                                                <option value="ANNUAL">
                                                    Anual
                                                </option>
                                            </select>
                                        </div>
                                        <div className="form-control w-full">
                                            <label className="label">
                                                <span className="label-text">
                                                    Cobrar por quantas vezes
                                                </span>
                                            </label>
                                            <input
                                                type="number"
                                                className="input input-bordered w-full"
                                                placeholder="Infinito"
                                                min={1}
                                                value={
                                                    formData.recurringMaxOccurrences
                                                }
                                                onChange={e =>
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        recurringMaxOccurrences:
                                                            e.target.value,
                                                    }))
                                                }
                                            />
                                            <label className="label">
                                                <span className="label-text-alt text-base-content/50">
                                                    Deixe vazio para cobrar
                                                    indefinidamente
                                                </span>
                                            </label>
                                        </div>
                                    </div>
                                )}
                            </FormSection>
                        }

                        {/* Dados PIX (somente leitura na edição, gerados pelo gateway) - apenas RECEIVABLE */}
                        {isReceivable &&
                            isEditing &&
                            formData.paymentMethod === 'PIX' &&
                            (formData.pixKey ||
                                formData.pixCopyPaste ||
                                formData.pixQrCode) && (
                                <FormSection
                                    id="secao-pix"
                                    icon={<MdQrCode className="text-lg" />}
                                    title="Dados PIX"
                                    subtitle="Gerado pelo gateway"
                                >
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {formData.pixKey && (
                                            <div className="form-control w-full">
                                                <label className="label">
                                                    <span className="label-text">
                                                        Chave PIX
                                                    </span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="input input-bordered w-full bg-base-200"
                                                    value={formData.pixKey}
                                                    readOnly
                                                />
                                            </div>
                                        )}

                                        {formData.pixExpirationDate && (
                                            <div className="form-control w-full">
                                                <label className="label">
                                                    <span className="label-text">
                                                        Expiração do PIX
                                                    </span>
                                                </label>
                                                <input
                                                    type="date"
                                                    className="input input-bordered w-full bg-base-200"
                                                    value={
                                                        formData.pixExpirationDate
                                                    }
                                                    readOnly
                                                />
                                            </div>
                                        )}

                                        {formData.pixCopyPaste && (
                                            <div className="form-control w-full md:col-span-2">
                                                <label className="label">
                                                    <span className="label-text">
                                                        PIX Copia e Cola
                                                    </span>
                                                </label>
                                                <textarea
                                                    className="textarea textarea-bordered w-full h-20 bg-base-200"
                                                    value={
                                                        formData.pixCopyPaste
                                                    }
                                                    readOnly
                                                />
                                            </div>
                                        )}

                                        {formData.pixQrCode && (
                                            <div className="form-control w-full md:col-span-2">
                                                <label className="label">
                                                    <span className="label-text">
                                                        QR Code PIX (texto)
                                                    </span>
                                                </label>
                                                <textarea
                                                    className="textarea textarea-bordered w-full h-20 bg-base-200"
                                                    value={formData.pixQrCode}
                                                    readOnly
                                                />
                                            </div>
                                        )}
                                    </div>
                                </FormSection>
                            )}

                        {/* Dados Boleto (somente leitura na edição, gerados pelo gateway) - apenas RECEIVABLE */}
                        {isReceivable &&
                            isEditing &&
                            formData.paymentMethod === 'BOLETO' &&
                            (formData.bankSlipUrl ||
                                formData.nossoNumero ||
                                formData.barcode ||
                                formData.digitableLine) && (
                                <FormSection
                                    id="secao-boleto"
                                    icon={<MdPayment className="text-lg" />}
                                    title="Dados do boleto"
                                    subtitle="Gerado pelo gateway"
                                >
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        {formData.bankSlipUrl && (
                                            <div className="form-control w-full md:col-span-2">
                                                <label className="label">
                                                    <span className="label-text">
                                                        URL do Boleto
                                                    </span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="input input-bordered w-full bg-base-200"
                                                    value={formData.bankSlipUrl}
                                                    readOnly
                                                />
                                            </div>
                                        )}

                                        {formData.nossoNumero && (
                                            <div className="form-control w-full">
                                                <label className="label">
                                                    <span className="label-text">
                                                        Nosso Número
                                                    </span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="input input-bordered w-full bg-base-200"
                                                    value={formData.nossoNumero}
                                                    readOnly
                                                />
                                            </div>
                                        )}

                                        {formData.barcode && (
                                            <div className="form-control w-full">
                                                <label className="label">
                                                    <span className="label-text">
                                                        Código de Barras
                                                    </span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="input input-bordered w-full bg-base-200"
                                                    value={formData.barcode}
                                                    readOnly
                                                />
                                            </div>
                                        )}

                                        {formData.digitableLine && (
                                            <div className="form-control w-full md:col-span-2">
                                                <label className="label">
                                                    <span className="label-text">
                                                        Linha Digitável
                                                    </span>
                                                </label>
                                                <input
                                                    type="text"
                                                    className="input input-bordered w-full bg-base-200"
                                                    value={
                                                        formData.digitableLine
                                                    }
                                                    readOnly
                                                />
                                            </div>
                                        )}
                                    </div>
                                </FormSection>
                            )}

                        {/* Observações */}
                        <FormSection
                            id="secao-observacoes"
                            icon={<MdNotes className="text-lg" />}
                            title="Observações"
                            subtitle="Anotações internas sobre o lançamento"
                        >
                            <div className="form-control w-full">
                                <textarea
                                    name="notes"
                                    placeholder={
                                        isPayable
                                            ? 'Observações sobre a conta...'
                                            : 'Observações sobre a cobrança...'
                                    }
                                    className="textarea textarea-bordered w-full h-32"
                                    value={formData.notes}
                                    onChange={handleChange}
                                />
                            </div>
                        </FormSection>

                        {/* Anexos */}
                        <FormSection
                            id="secao-anexos"
                            icon={<MdLink className="text-lg" />}
                            title="Anexos"
                            subtitle="Comprovantes e documentos do lançamento"
                        >
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                <div className="form-control w-full">
                                    <label className="label py-1">
                                        <span className="label-text text-xs">
                                            Arquivo
                                        </span>
                                    </label>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".pdf,.jpg,.jpeg,.png,.webp,.doc,.docx,.xls,.xlsx"
                                        className="file-input file-input-bordered file-input-sm w-full"
                                        onChange={e =>
                                            setAttachmentFile(
                                                e.target.files?.[0] || null
                                            )
                                        }
                                    />
                                    <label className="label py-0">
                                        <span className="label-text-alt text-base-content/40">
                                            PDF, imagens, DOC, XLS (max. 10MB)
                                        </span>
                                    </label>
                                </div>
                                <div className="form-control w-full">
                                    <label className="label py-1">
                                        <span className="label-text text-xs">
                                            Descrição
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Ex: Comprovante de pagamento"
                                        className="input input-bordered input-sm w-full"
                                        value={attachmentDescription}
                                        onChange={e =>
                                            setAttachmentDescription(
                                                e.target.value
                                            )
                                        }
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end mt-3">
                                <button
                                    type="button"
                                    onClick={handleAddAttachment}
                                    disabled={
                                        !attachmentFile || savingAttachment
                                    }
                                    className="btn btn-sm btn-outline btn-secondary gap-1"
                                >
                                    {savingAttachment ? (
                                        <span className="loading loading-spinner loading-xs" />
                                    ) : (
                                        <MdUploadFile className="text-base" />
                                    )}
                                    Adicionar anexo
                                </button>
                            </div>

                            {formData.attachments.length === 0 &&
                            pendingFiles.length === 0 ? (
                                <p className="text-sm text-base-content/50 mt-4">
                                    Nenhum anexo adicionado.
                                </p>
                            ) : (
                                <div className="flex flex-col gap-2 mt-4">
                                    {formData.attachments.map((att, index) => (
                                        <div
                                            key={`saved-${index}`}
                                            className="flex items-center gap-3 p-3 bg-base-200/30 rounded-lg border border-base-300/30"
                                        >
                                            <MdLink className="text-lg text-secondary shrink-0" />
                                            <div className="min-w-0 flex-1">
                                                <a
                                                    href={att.url}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="link link-hover font-medium text-sm truncate block"
                                                >
                                                    {att.name || 'Anexo'}
                                                </a>
                                                {att.description ? (
                                                    <p className="text-xs text-base-content/60 truncate">
                                                        {att.description}
                                                    </p>
                                                ) : null}
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleRemoveAttachment(
                                                        index
                                                    )
                                                }
                                                disabled={savingAttachment}
                                                className="btn btn-sm btn-error btn-outline"
                                            >
                                                <MdDelete className="text-lg" />
                                            </button>
                                        </div>
                                    ))}

                                    {pendingFiles.map((pending, index) => (
                                        <div
                                            key={`pending-${index}`}
                                            className="flex items-center gap-3 p-3 bg-base-200/30 rounded-lg border border-dashed border-base-300"
                                        >
                                            <MdUploadFile className="text-lg text-base-content/50 shrink-0" />
                                            <div className="min-w-0 flex-1">
                                                <p className="font-medium text-sm truncate">
                                                    {pending.file.name}
                                                </p>
                                                {pending.description ? (
                                                    <p className="text-xs text-base-content/60 truncate">
                                                        {pending.description}
                                                    </p>
                                                ) : null}
                                            </div>
                                            <span className="badge badge-ghost badge-sm">
                                                Envia ao salvar
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    handleRemovePendingFile(
                                                        index
                                                    )
                                                }
                                                className="btn btn-sm btn-error btn-outline"
                                            >
                                                <MdDelete className="text-lg" />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </FormSection>

                        {/* Composição da solicitação agrupada, enquanto ela está
                            sendo configurada. É aqui que o financeiro tira um
                            atendimento que o parceiro ainda não realizou — depois
                            de enviada, o valor não pode mais mudar debaixo de quem
                            aprova. */}
                        {awaitingBatchSetup && id && (
                            <FormSection
                                id="secao-repasses-agrupados"
                                icon={<MdPayment className="text-lg" />}
                                title="Repasses agrupados"
                                subtitle={`${batchSummary.total} lançamento${batchSummary.total === 1 ? '' : 's'} somados nesta solicitação`}
                            >
                                <div className="alert alert-info text-xs mb-3">
                                    <span>
                                        Retirar um lançamento não o cancela: ele
                                        volta a ficar em aberto e entra no próximo
                                        agrupamento deste destinatário. Só é
                                        possível antes de enviar para aprovação, e
                                        enquanto o pagamento não estiver dividido em
                                        formas.
                                    </span>
                                </div>
                                <PayoutBatchItemsList
                                    batchId={parseInt(id!, 10)}
                                    allowRemove
                                    removingId={removingItemId}
                                    onRemove={setItemToRemove}
                                    reloadToken={batchReloadToken}
                                    onSummary={handleBatchSummary}
                                />
                            </FormSection>
                        )}

                        {/* Ações: botões com largura própria, e o que falta
                            preencher dito em texto — o tooltip sozinho só
                            aparecia para quem passasse o mouse no lugar certo. */}
                        <div className="flex flex-col gap-3 rounded-2xl border border-base-300 bg-base-100 p-4 sm:flex-row sm:items-center sm:justify-between">
                            <p className="text-xs text-base-content/50">
                                {isFormValid()
                                    ? 'Tudo pronto para salvar.'
                                    : `Falta preencher: ${getMissingFields().join(', ')}`}
                            </p>
                            <div className="flex flex-col-reverse gap-2 sm:flex-row">
                                <button
                                    type="button"
                                    className="btn btn-ghost sm:w-36"
                                    onClick={() =>
                                        onBack
                                            ? onBack()
                                            : navigate('/financial/billings')
                                    }
                                >
                                    Cancelar
                                </button>
                                {awaitingBatchSetup && (
                                    <button
                                        type="button"
                                        className="btn btn-outline btn-secondary gap-2"
                                        onClick={handleSubmitBatchForApproval}
                                        disabled={loading || submittingBatch}
                                        title="Grava o que está na tela e manda a solicitação para a fila de aprovação"
                                    >
                                        {loading || submittingBatch ? (
                                            <span className="loading loading-spinner loading-sm" />
                                        ) : null}
                                        Salvar e enviar para aprovação
                                    </button>
                                )}
                                <button
                                    type="submit"
                                    className="btn btn-secondary sm:w-40"
                                    disabled={loading || !isFormValid()}
                                >
                                    {loading ? (
                                        <span className="loading loading-spinner"></span>
                                    ) : isFromScheduling ? (
                                        'Finalizar'
                                    ) : isEditing ? (
                                        'Atualizar'
                                    ) : (
                                        'Salvar'
                                    )}
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </form>

            <ConfirmModal
                isOpen={itemToRemove !== null}
                onClose={() => setItemToRemove(null)}
                onConfirm={() =>
                    itemToRemove !== null && handleRemoveBatchItem(itemToRemove)
                }
                loading={removingItemId !== null}
                title="Retirar da solicitação"
                message={`O lançamento #${itemToRemove} sai desta solicitação e volta a ficar em aberto — ele entra no próximo agrupamento deste destinatário. O valor da conta e o demonstrativo são refeitos.`}
                confirmButtonText="Retirar"
            />
        </div>
    )
}
